/*
  Cactus Corn: custom label builder for corporate gift bags (a 3x5 label on a clear bag).
  Paired with sections/cc-label-builder.liquid, which prints the settings, flavors, and asset links as
  JSON, and with assets/cc-label-templates.json (label styles, fonts, and art).
  Buyers only make choices; the label lays itself out: their logo big at the top, the flavor and
  ingredients under it, and a small Cactus Corn logo, the net weight, and an optional QR code along the
  bottom. The label is laid out in points (72 per inch), so sizes on screen match print.
  The proof PDF code lives in cc-label-export.js and only loads when someone asks for a proof.
*/
(() => {
  'use strict';

  const PT = 72;
  const TAU = Math.PI * 2;
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const clamp = (v, lo, hi) => Math.min(hi, Math.max(lo, v));
  const num = (v, fallback = NaN) => { const n = parseFloat(String(v == null ? '' : v).replace(/[^0-9.\-]/g, '')); return Number.isFinite(n) ? n : fallback; };
  const toCents = v => { const n = num(v); return Number.isFinite(n) ? Math.round(n * 100) : NaN; };
  const money = c => Number.isFinite(c) ? '$' + (c / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : 'TBD';
  const plural = (n, one, many) => `${n} ${n === 1 ? one : many}`;
  const debounce = (fn, ms) => { let t = 0; return (...a) => { clearTimeout(t); t = setTimeout(() => fn(...a), ms); }; };
  const store = {
    get(key, fallback) { try { const v = localStorage.getItem(key); return v ? JSON.parse(v) : fallback; } catch (e) { return fallback; } },
    set(key, value) { try { localStorage.setItem(key, JSON.stringify(value)); return true; } catch (e) { return false; } },
    del(key) { try { localStorage.removeItem(key); } catch (e) { /* storage blocked */ } }
  };

  function h(tag, attrs, ...kids) {
    const node = document.createElement(tag);
    for (const [k, v] of Object.entries(attrs || {})) {
      if (v == null || v === false) continue;
      if (k === 'class') node.className = v;
      else if (k === 'dataset') Object.assign(node.dataset, v);
      else if (k === 'style' && typeof v === 'object') Object.assign(node.style, v);
      else if (k.startsWith('on') && typeof v === 'function') node.addEventListener(k.slice(2), v);
      else node.setAttribute(k, v === true ? '' : v);
    }
    for (const kid of kids.flat()) {
      if (kid == null || kid === false) continue;
      node.append(kid instanceof Node ? kid : document.createTextNode(String(kid)));
    }
    return node;
  }

  // UMD bundles register with an AMD loader instead of a global when the page has one, so hide it while
  // they load. Scripts load one at a time so the loader is always put back.
  const scriptCache = new Map();
  let scriptChain = Promise.resolve();
  function loadScript(url) {
    if (!scriptCache.has(url)) {
      const p = scriptChain.then(() => new Promise((resolve, reject) => {
        const amd = window.define && window.define.amd ? window.define : null;
        if (amd) window.define = undefined;
        const s = document.createElement('script');
        s.src = url;
        s.onload = () => { if (amd) window.define = amd; resolve(); };
        s.onerror = () => { if (amd) window.define = amd; reject(new Error('Could not load ' + url)); };
        document.head.append(s);
      }));
      scriptChain = p.catch(() => {});
      scriptCache.set(url, p);
    }
    return scriptCache.get(url);
  }

  const designIdAlphabet = 'ABCDEFGHJKMNPQRSTUVWXYZ23456789';
  function newDesignId() {
    const bytes = new Uint8Array(6);
    crypto.getRandomValues(bytes);
    return 'CC-' + Array.from(bytes, b => designIdAlphabet[b % designIdAlphabet.length]).join('');
  }
  const dataUrlToBytes = url => { const b = atob(url.split(',')[1]); const u = new Uint8Array(b.length); for (let i = 0; i < b.length; i++) u[i] = b.charCodeAt(i); return u; };
  const blobToDataUrl = blob => new Promise((res, rej) => { const r = new FileReader(); r.onload = () => res(r.result); r.onerror = rej; r.readAsDataURL(blob); });
  const loadImage = src => new Promise((res, rej) => { const i = new Image(); i.onload = () => res(i); i.onerror = rej; i.src = src; });

  /* ---------- Color ---------- */
  const isHex = v => /^#[0-9a-f]{6}$/i.test(v);
  const normHex = v => { let s = String(v || '').trim(); if (!s.startsWith('#')) s = '#' + s; if (/^#[0-9a-f]{3}$/i.test(s)) s = '#' + s.slice(1).split('').map(c => c + c).join(''); return isHex(s) ? s.toUpperCase() : null; };
  const rgbOf = hex => { const n = parseInt(hex.slice(1), 16); return [n >> 16, (n >> 8) & 255, n & 255]; };
  const hexOf = (r, g, b) => '#' + [r, g, b].map(v => Math.round(clamp(v, 0, 255)).toString(16).padStart(2, '0')).join('').toUpperCase();
  function contrastRatio(a, b) {
    const lum = hex => rgbOf(hex).map(c => { c /= 255; return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4); }).reduce((s, c, i) => s + c * [0.2126, 0.7152, 0.0722][i], 0);
    const [l1, l2] = [lum(a), lum(b)].sort((x, y) => y - x);
    return (l1 + 0.05) / (l2 + 0.05);
  }
  const darken = (hex, t) => hexOf(...rgbOf(hex).map(c => c * (1 - t)));
  // Darkens a color until it reads on the background (3 to 1, the floor for bold print type).
  function readableOn(fg, bg) {
    let c = fg;
    for (let i = 0; i < 14 && contrastRatio(c, bg) < 3; i++) c = darken(c, 0.12);
    return c;
  }
  // The main colors in a logo, most used first. White, black, and grays are skipped.
  function logoColors(source) {
    const sw = source.naturalWidth || source.width, sh = source.naturalHeight || source.height;
    const s = Math.min(1, 220 / Math.max(sw, sh));
    const c = document.createElement('canvas');
    c.width = Math.max(1, Math.round(sw * s)); c.height = Math.max(1, Math.round(sh * s));
    const g = c.getContext('2d', { willReadFrequently: true });
    g.drawImage(source, 0, 0, c.width, c.height);
    const d = g.getImageData(0, 0, c.width, c.height).data;
    const bins = new Map();
    let total = 0;
    for (let i = 0; i < d.length; i += 4) {
      if (d[i + 3] < 200) continue;
      const r = d[i], gr = d[i + 1], b = d[i + 2];
      if (Math.max(r, gr, b) - Math.min(r, gr, b) < 30) continue;
      const key = (r >> 4) << 8 | (gr >> 4) << 4 | (b >> 4);
      const bin = bins.get(key) || { n: 0, r: 0, g: 0, b: 0 };
      bin.n++; bin.r += r; bin.g += gr; bin.b += b;
      bins.set(key, bin); total++;
    }
    const picked = [];
    for (const bin of [...bins.values()].sort((x, y) => y.n - x.n)) {
      if (bin.n < total * 0.03 || picked.length === 4) break;
      const col = [bin.r / bin.n, bin.g / bin.n, bin.b / bin.n];
      if (picked.some(p => Math.hypot(p[0] - col[0], p[1] - col[1], p[2] - col[2]) < 70)) continue;
      picked.push(col);
    }
    return picked.map(p => hexOf(...p));
  }

  /* ------------------------------------------------------------------
     Drawn popcorn for stand-in bags (copied from the tin builder)
     ------------------------------------------------------------------ */
  function mulberry32(a) {
    return () => { a |= 0; a = a + 0x6D2B79F5 | 0; let t = Math.imul(a ^ a >>> 15, 1 | a); t = t + Math.imul(t ^ t >>> 7, 61 | t) ^ t; return ((t ^ t >>> 14) >>> 0) / 4294967296; };
  }
  function hashStr(s) { let x = 2166136261; for (let i = 0; i < s.length; i++) { x ^= s.charCodeAt(i); x = Math.imul(x, 16777619); } return x >>> 0; }
  const K = (base, light, dark) => ({ base, light, dark });
  const CARAMEL = [K('#A95D1C', '#E9A45C', '#5A2A0B'), 3];
  const CARAMEL_2 = [K('#B86A24', '#F0B46C', '#63300E'), 2];
  const CHEESE = [K('#F59A22', '#FFD27F', '#B8590A'), 3];
  const LOOKS = {
    kettle: { gap: '#6B5427', kernels: [[K('#F3E4BD', '#FFFCF2', '#C7A767'), 3], [K('#ECD6A6', '#FFF8E6', '#BC9A58'), 2]], top: [['glints', 170]] },
    caramel: { gap: '#3A1906', kernels: [CARAMEL, CARAMEL_2], gloss: 0.65 },
    street: { gap: '#6B4716', kernels: [[K('#F0CE86', '#FFF5DA', '#C69A48'), 3], [K('#E9C274', '#FFF0CC', '#BA8C3A'), 2]], top: [['dust', 620, 'rgba(190,52,30,0.5)', 0.8, 2.2], ['flecks', 80, '#4F8F2E']] },
    wcj: { gap: '#6A6247', kernels: [[K('#F6F1E1', '#FFFFFF', '#CFC4A0'), 1]], top: [['dust', 420, 'rgba(255,251,236,0.75)', 0.8, 2], ['jalapeno', 70]] },
    bday: { gap: '#7B6648', kernels: [[K('#FBF5E8', '#FFFFFF', '#D9C8A7'), 1]], top: [['sprinkles', 190]] },
    pear: { gap: '#5C1135', kernels: [[K('#E15A9C', '#FFC9E2', '#93225A'), 3], [K('#D8467F', '#FBA9CD', '#841B48'), 2]], gloss: 0.3, top: [['glints', 130]] },
    cheese: { gap: '#6C3404', kernels: [CHEESE, [K('#F8AA35', '#FFDC98', '#C2660F'), 2]], top: [['dust', 380, 'rgba(255,200,100,0.55)', 0.8, 2]] },
    hotchoc: { gap: '#1D0E07', kernels: [[K('#5E3624', '#A06F54', '#2A150B'), 3], [K('#704229', '#AC7D5E', '#341A0E'), 2]], gloss: 0.45, top: [['drizzle', 26, 'rgba(44,22,11,0.92)'], ['marshmallows', 46]] },
    candycane: { gap: '#6A5947', kernels: [[K('#F8F2E7', '#FFFFFF', '#D3C4AB'), 1]], top: [['peppermint', 170], ['drizzle', 12, 'rgba(214,40,57,0.5)']] }
  };
  const lookFor = id => LOOKS[id] || LOOKS.kettle;
  const rgba = (hex, a) => { const n = parseInt(hex.slice(1), 16); return `rgba(${n >> 16},${(n >> 8) & 255},${n & 255},${a})`; };
  function roundRectPath(g, x, y, w, hgt, r) {
    g.beginPath(); g.moveTo(x + r, y);
    g.arcTo(x + w, y, x + w, y + hgt, r); g.arcTo(x + w, y + hgt, x, y + hgt, r);
    g.arcTo(x, y + hgt, x, y, r); g.arcTo(x, y, x + w, y, r); g.closePath();
  }
  function blobPath(x, y, r, rand) {
    const p = new Path2D(), pts = 7;
    for (let i = 0; i < pts; i++) { const a = i / pts * TAU, rr = r * (0.7 + rand() * 0.45); i ? p.lineTo(x + Math.cos(a) * rr, y + Math.sin(a) * rr) : p.moveTo(x + Math.cos(a) * rr, y + Math.sin(a) * rr); }
    p.closePath();
    return p;
  }
  const TOPPINGS = {
    glints(g, S, rand, n) { for (let i = 0; i < n; i++) { const s = 1 + rand() * 1.8; g.fillStyle = `rgba(255,255,255,${0.5 + rand() * 0.45})`; g.fillRect(rand() * S, rand() * S, s, s); } },
    dust(g, S, rand, n, color, min, max) { g.fillStyle = color; for (let i = 0; i < n; i++) { g.beginPath(); g.arc(rand() * S, rand() * S, min + rand() * (max - min), 0, TAU); g.fill(); } },
    flecks(g, S, rand, n, color) { g.fillStyle = color; for (let i = 0; i < n; i++) { g.beginPath(); g.ellipse(rand() * S, rand() * S, 1.4 + rand() * 2.2, 0.9 + rand() * 1.2, rand() * Math.PI, 0, TAU); g.fill(); } },
    jalapeno(g, S, rand, n) {
      const cols = ['#3E8B3A', '#58A844', '#2F7431'];
      for (let i = 0; i < n; i++) { const p = blobPath(rand() * S, rand() * S, 2.6 + rand() * 3.4, rand); g.fillStyle = cols[Math.floor(rand() * cols.length)]; g.fill(p); }
    },
    sprinkles(g, S, rand, n) {
      const cols = ['#F2558E', '#3FA7E0', '#F7C933', '#5CC46A', '#9B6BD6', '#F7883B', '#FFFFFF'];
      for (let i = 0; i < n; i++) { const L = 10 + rand() * 7, W = 3.6; g.save(); g.translate(rand() * S, rand() * S); g.rotate(rand() * Math.PI); g.fillStyle = cols[Math.floor(rand() * cols.length)]; roundRectPath(g, -L / 2, -W / 2, L, W, W / 2); g.fill(); g.restore(); }
    },
    marshmallows(g, S, rand, n) {
      for (let i = 0; i < n; i++) { const s = 15 + rand() * 8; g.save(); g.translate(rand() * S, rand() * S); g.rotate(rand() * Math.PI); g.fillStyle = '#F4EEE3'; roundRectPath(g, -s / 2, -s / 2, s, s, s * 0.3); g.fill(); g.restore(); }
    },
    drizzle(g, S, rand, n, color) {
      g.save(); g.strokeStyle = color; g.lineCap = 'round';
      for (let i = 0; i < n; i++) { const x = rand() * S, y = rand() * S, len = 90 + rand() * 160, a = rand() * TAU; g.lineWidth = 2.6 + rand() * 2.4; g.beginPath(); g.moveTo(x, y); g.bezierCurveTo(x + (rand() - 0.5) * 120, y + (rand() - 0.5) * 120, x + Math.cos(a) * len, y + Math.sin(a) * len, x + Math.cos(a) * len, y + Math.sin(a) * len); g.stroke(); }
      g.restore();
    },
    peppermint(g, S, rand, n) {
      const cols = ['#D62839', '#D62839', '#E34A5B', '#FFFFFF', '#F4A7B9'];
      for (let i = 0; i < n; i++) { const r = 3 + rand() * 4.5; g.save(); g.translate(rand() * S, rand() * S); g.rotate(rand() * TAU); g.fillStyle = cols[Math.floor(rand() * cols.length)]; g.beginPath(); g.moveTo(-r, -r * 0.3); g.lineTo(r * 0.8, -r * 0.6); g.lineTo(r * 0.4, r * 0.7); g.closePath(); g.fill(); g.restore(); }
    }
  };
  function drawKernel(g, k, rand, look, back) {
    const { x, y, r, col } = k;
    const lobes = 6 + Math.floor(rand() * 4), rot = rand() * TAU, parts = [];
    for (let i = 0; i < lobes; i++) {
      const a = rot + (i / lobes) * TAU + (rand() - 0.5) * 0.8, d = r * (0.3 + rand() * 0.36), pr = r * (0.28 + rand() * 0.24);
      parts.push({ x: x + Math.cos(a) * d, y: y + Math.sin(a) * d, rx: pr, ry: pr * (0.75 + rand() * 0.25), rot: rand() * TAU });
    }
    const cr = r * (0.5 + rand() * 0.12);
    parts.push({ x: x + (rand() - 0.5) * r * 0.2, y: y + (rand() - 0.5) * r * 0.2, rx: cr, ry: cr * (0.82 + rand() * 0.18), rot: rand() * TAU });
    const shape = new Path2D();
    for (const p of parts) { shape.moveTo(p.x + p.rx * Math.cos(p.rot), p.y + p.rx * Math.sin(p.rot)); shape.ellipse(p.x, p.y, p.rx, p.ry, p.rot, 0, TAU); }
    if (back) { g.fillStyle = col.base; g.fill(shape); return; }
    g.save(); g.translate(r * 0.08, r * 0.14); g.fillStyle = 'rgba(0,0,0,0.25)'; g.fill(shape); g.restore();
    const bx = x - r * 1.7, by = y - r * 1.7, bs = r * 3.4;
    g.save(); g.clip(shape);
    g.fillStyle = col.base; g.fillRect(bx, by, bs, bs);
    for (const p of parts) {
      const gr = g.createRadialGradient(p.x - p.rx * 0.25, p.y - p.ry * 0.3, 0, p.x, p.y, Math.max(p.rx, p.ry));
      gr.addColorStop(0, rgba(col.light, 0.95)); gr.addColorStop(0.55, rgba(col.light, 0.22)); gr.addColorStop(0.82, rgba(col.dark, 0)); gr.addColorStop(1, rgba(col.dark, 0.6));
      g.fillStyle = gr; g.beginPath(); g.ellipse(p.x, p.y, p.rx, p.ry, p.rot, 0, TAU); g.fill();
    }
    g.restore();
    if (look.gloss && rand() < look.gloss) { const p = parts[Math.floor(rand() * parts.length)]; g.fillStyle = 'rgba(255,255,255,0.42)'; g.beginPath(); g.ellipse(p.x - p.rx * 0.3, p.y - p.ry * 0.38, p.rx * 0.34, p.ry * 0.14, -0.6, 0, TAU); g.fill(); }
  }
  const TEX = 720;
  const texCache = new Map();
  // Bigger sizes keep the same kernel size and add more kernels, so a whole bag fills without visible tiling.
  function popcornTexture(lookId, size = TEX) {
    const key = lookId + ':' + size;
    let c = texCache.get(key);
    if (c) return c;
    const look = lookFor(lookId), area = (size / TEX) * (size / TEX);
    c = document.createElement('canvas'); c.width = c.height = size;
    const g = c.getContext('2d'), rand = mulberry32(hashStr(lookId));
    g.fillStyle = look.gap; g.fillRect(0, 0, size, size);
    const total = look.kernels.reduce((s, [, w]) => s + w, 0);
    const pick = () => { let r = rand() * total; for (const [k, w] of look.kernels) { r -= w; if (r <= 0) return k; } return look.kernels[0][0]; };
    const kernel = () => ({ x: -40 + rand() * (size + 80), y: -40 + rand() * (size + 80), r: TEX * (0.042 + rand() * 0.028), col: pick() });
    for (let i = 0; i < 120 * area; i++) drawKernel(g, kernel(), rand, look, true);
    for (let i = 0; i < 170 * area; i++) drawKernel(g, kernel(), rand, look, false);
    g.fillStyle = 'rgba(0,0,0,0.3)'; g.fillRect(0, 0, size, size);
    for (let i = 0; i < 330 * area; i++) drawKernel(g, kernel(), rand, look, false);
    for (const [kind, ...args] of look.top || []) TOPPINGS[kind](g, size, rand, ...args.map((a, j) => j === 0 && typeof a === 'number' ? Math.round(a * area) : a));
    texCache.set(key, c);
    return c;
  }
  function paintSwatch(c, lookId) {
    const g = c.getContext('2d'), w = c.width, tex = popcornTexture(lookId), src = TEX * 0.36;
    g.clearRect(0, 0, w, w); g.save(); g.beginPath(); g.arc(w / 2, w / 2, w / 2, 0, TAU); g.clip();
    g.drawImage(tex, (TEX - src) / 2, (TEX - src) / 2, src, src, 0, 0, w, w); g.restore();
  }

  /* ------------------------------------------------------------------
     Settings from the section
     ------------------------------------------------------------------ */
  function readConfig(root) {
    const raw = JSON.parse(root.querySelector('[data-cclb-config]').textContent);
    const p = raw.pricing || {};
    const breaks = (p.breaks || []).map(v => Math.round(num(v)));
    const table = list => breaks.map((min, i) => ({ min, max: breaks[i + 1] ? breaks[i + 1] - 1 : null, cents: toCents((list || [])[i]) }));
    const seasonOf = (a, b) => {
      const re = /^(\d{1,2})-(\d{1,2})$/, x = re.exec(String(a || '').trim()), y = re.exec(String(b || '').trim());
      return x && y ? { from: [+x[1], +x[2]], to: [+y[1], +y[2]] } : null;
    };
    const flavors = (raw.flavors || []).filter(f => f && f.enabled !== false && f.name).map(f => ({
      ...f, identity: (f.identity || f.name || '').trim(), ingredients: (f.ingredients || '').trim(), contains: (f.contains || '').trim(),
      season: seasonOf(f.seasonStart, f.seasonEnd)
    }));
    const L = raw.limits || {};
    return {
      ...raw,
      products: (raw.products || []).filter(x => x.enabled).map(x => ({
        ...x, faceIn: [num(x.faceIn && x.faceIn[0], 6), num(x.faceIn && x.faceIn[1], 8)], labelTopIn: num(x.labelTopIn, 1.5)
      })),
      pricing: {
        minQty: Math.round(num(p.minQty, 50)),
        tables: { snack: table(p.snack), full: table(p.full) },
        setupCents: Number.isFinite(toCents(p.setupFee)) ? toCents(p.setupFee) : 0,
        leadTimeText: p.leadTimeText || '', leadTimeDays: Math.round(num(p.leadTimeDays, 21)), rushText: p.rushText || '',
        proofTurnaround: p.proofTurnaround || 'one business day', confirmed: !!p.confirmed
      },
      print: {
        trimIn: [3, 5], bleedIn: num(raw.print && raw.print.bleedIn, 0.125), safeIn: num(raw.print && raw.print.safeIn, 0.125),
        dpi: Math.round(num(raw.print && raw.print.dpi, 300)), printerName: (raw.print && raw.print.printerName) || '',
        colorNote: (raw.print && raw.print.colorNote) || 'RGB file, printer to confirm color conversion.', confirmed: !!(raw.print && raw.print.confirmed)
      },
      fda: {
        ...(raw.fda || {}),
        netWtMinTypeIn: num(raw.fda && raw.fda.netWtMinTypeIn, 0.1875),
        netWtFormat: (raw.fda && raw.fda.netWtFormat) || 'NET WT. {oz} OZ ({g} G)',
        claims: String((raw.fda && raw.fda.claims) || '').split(/\n+/).map(s => s.trim().toLowerCase()).filter(Boolean)
      },
      limits: {
        maxUploadMb: num(L.maxUploadMb, 10), messageMaxChars: Math.round(num(L.messageMaxChars, 60)),
        messageMaxLines: Math.round(num(L.messageMaxLines, 2)), qrMinIn: num(L.qrMinIn, 0.8), qrCaption: L.qrCaption == null ? 'Scan me' : String(L.qrCaption)
      },
      flavors
    };
  }

  /* ------------------------------------------------------------------
     One builder per section
     ------------------------------------------------------------------ */
  async function initBuilder(root) {
    let cfg;
    try { cfg = readConfig(root); } catch (err) { console.error('Label builder: could not read its settings.', err); return; }
    const A = name => new URL(cfg.assets[name], location.href).href;
    const sid = cfg.sectionId;
    const today = new Date();
    const inSeason = (f, d = today) => {
      if (!f.season) return true;
      const md = (d.getMonth() + 1) * 100 + d.getDate(), a = f.season.from[0] * 100 + f.season.from[1], b = f.season.to[0] * 100 + f.season.to[1];
      return a <= b ? md >= a && md <= b : md >= a || md <= b;
    };
    const FLAVORS = cfg.flavors.filter(f => inSeason(f));
    const byFlavor = Object.fromEntries(FLAVORS.map(f => [f.id, f]));

    let lib;
    try { lib = await (await fetch(A('cc-label-templates.json'))).json(); } catch (err) { console.error('Label builder: could not load its styles.', err); return; }
    const fontById = Object.fromEntries(lib.fonts.map(f => [f.id, f]));
    const fontByFamily = Object.fromEntries(lib.fonts.map(f => [f.family, f]));
    const STYLES = lib.styles || [];
    const byStyle = Object.fromEntries(STYLES.map(s => [s.id, s]));

    const STEPS = [
      { id: 'bag', label: 'Bag', title: 'Which bag?' },
      { id: 'logo', label: 'Logo', title: 'Add your logo.' },
      { id: 'flavor', label: 'Flavor', title: 'Pick a flavor. We pop it fresh in Phoenix.' },
      { id: 'style', label: 'Style', title: 'Pick a style.' },
      { id: 'qr', label: 'QR code', title: 'Want a QR code?' },
      { id: 'proof', label: 'Proof', title: 'Get your free proof.' }
    ];
    const params = new URLSearchParams(location.search);
    const state = {
      step: 0,
      productId: (cfg.products[0] || {}).id,
      styleId: (STYLES[0] || {}).id,
      flavorId: (FLAVORS[0] || {}).id,
      color: null,
      colorPicked: false,
      logoColors: [],
      message: '',
      qrWanted: null,
      qrUrl: '',
      qrCaption: cfg.limits.qrCaption,
      logo: null,
      logoObj: null,
      logoScale: 1,
      qty: Math.max(100, cfg.pricing.minQty),
      view: 'bag',
      designId: null,
      parentDesignId: null,
      busy: false,
      submitted: null
    };
    const product = () => cfg.products.find(p => p.id === state.productId) || cfg.products[0];
    const currentStyle = () => byStyle[state.styleId] || STYLES[0];
    const flavor = () => byFlavor[state.flavorId] || FLAVORS[0];
    const qrOn = () => state.qrWanted === true;
    const hasMessage = () => !!state.message.trim();

    /* ---------- Analytics ---------- */
    const KLAVIYO_EVENTS = new Set(['label_builder_opened', 'bag_preview_viewed', 'proof_form_started', 'proof_requested', 'proof_request_failed', 'add_to_cart_clicked']);
    function track(name, extra) {
      const data = { design_id: state.designId, style_id: state.styleId, flavor: state.flavorId, bag: state.productId, step: STEPS[state.step].id, ...(extra || {}) };
      if (cfg.lead.testMode) data.test_mode = true;
      // In test mode GA4 gets debug_mode, which shows in DebugView and stays out of the normal reports.
      try { if (cfg.analytics.ga4 && typeof window.gtag === 'function') window.gtag('event', 'cc_label_' + name, cfg.lead.testMode ? { ...data, debug_mode: true } : data); } catch (e) { /* ignore */ }
      try { if (cfg.analytics.shopifyPixel && window.Shopify && Shopify.analytics && Shopify.analytics.publish) Shopify.analytics.publish('cc_label_' + name, data); } catch (e) { /* ignore */ }
      try { if (cfg.analytics.klaviyo && KLAVIYO_EVENTS.has(name) && window.klaviyo && !cfg.lead.testMode) window.klaviyo.push(['track', 'Label Builder ' + name.replace(/_/g, ' '), data]); } catch (e) { /* ignore */ }
      if (cfg.lead.testMode && window.console) console.debug('[label builder]', name, data);
    }

    /* ---------- Page skeleton ---------- */
    root.querySelector('[data-cclb-boot]')?.remove();
    const el = {};
    el.progressBar = h('span');
    el.sizeToggle = h('div', { class: 'cclb-seg cclb-seg--size', role: 'group', 'aria-label': 'Bag size' });
    el.viewBag = h('button', { type: 'button', 'aria-pressed': 'true', onclick: () => setView('bag') }, 'On the bag');
    el.viewLabel = h('button', { type: 'button', 'aria-pressed': 'false', onclick: () => setView('label') }, 'Up close');
    el.bagCanvas = h('canvas', { class: 'cclb-preview__bag', width: 1280, height: 1600, role: 'img', 'aria-label': 'Your label on the bag' });
    el.labelCanvas = h('canvas', { class: 'cclb-preview__label', width: 600, height: 1000, role: 'img', 'aria-label': 'Your label', hidden: true });
    el.previewNote = h('p', { class: 'cclb-preview__note' });
    el.download = h('button', { type: 'button', class: 'cclb-link', onclick: () => downloadMockup() }, 'Download this picture');
    el.estimateLine = h('p', { class: 'cclb-preview__estimate', 'aria-live': 'polite' });
    el.saveEmail = h('input', { type: 'email', id: `cclb-save-email-${sid}`, autocomplete: 'email', placeholder: 'you@company.com', 'aria-label': 'Your email' });
    el.saveStatus = h('p', { class: 'cclb-help cclb-save__status', role: 'status', 'aria-live': 'polite' });
    // Emailing the link needs a Klaviyo flow on the "Label Design Saved" event. Until the section setting says that flow
    // is live, the buyer gets the link to copy instead, so nobody waits for an email that never comes.
    const saveByEmail = !!cfg.lead.saveDesignEmail;
    el.saveLink = h('input', { type: 'text', readonly: true, id: `cclb-save-link-${sid}`, 'aria-label': 'Link to this design', onfocus: e => e.target.select() });
    el.saveForm = saveByEmail
      ? h('form', { class: 'cclb-save__form', novalidate: true, hidden: true },
        h('label', { class: 'cclb-help', for: `cclb-save-email-${sid}` }, 'We will email you a link that opens this design again. Nothing else, unless you ask.'),
        h('div', { class: 'cclb-save__row' }, el.saveEmail, h('button', { type: 'submit', class: 'cclb-btn cclb-btn--small' }, 'Send the link')),
        el.saveStatus)
      : h('div', { class: 'cclb-save__form', hidden: true },
        h('label', { class: 'cclb-help', for: `cclb-save-link-${sid}` }, 'This link opens your design again. Paste it into an email or a text to yourself. On this device your logo comes back too; on another one, add it again.'),
        h('div', { class: 'cclb-save__row' }, el.saveLink, h('button', { type: 'button', class: 'cclb-btn cclb-btn--small', onclick: () => copyDesignLink() }, 'Copy link')),
        el.saveStatus);
    el.saveToggle = h('button', { type: 'button', class: 'cclb-link', 'aria-expanded': 'false', onclick: () => {
      const open = el.saveForm.hidden; el.saveForm.hidden = !open; el.saveToggle.setAttribute('aria-expanded', String(open));
      if (!open) return;
      if (saveByEmail) el.saveEmail.focus();
      else { el.saveStatus.textContent = ''; el.saveLink.value = resumeLink(); el.saveLink.focus(); }
      track('save_design_opened');
    } }, saveByEmail ? 'Email me this design' : 'Save a link to this design');
    if (saveByEmail) el.saveForm.addEventListener('submit', e => { e.preventDefault(); emailDesign(); });
    el.saveWrap = h('div', { class: 'cclb-save' }, h('div', { class: 'cclb-save__links' }, el.download, el.saveToggle), el.saveForm);
    el.checks = h('ul', { class: 'cclb-checks', 'aria-label': 'Notes about your label', 'aria-live': 'polite' });
    el.panelTitle = h('h2', { class: 'cclb-panel__title', tabindex: '-1', id: `cclb-panel-title-${sid}` });
    el.panels = h('div', { class: 'cclb-panel__body' });
    el.back = h('button', { type: 'button', class: 'cclb-btn cclb-btn--ghost', onclick: () => goTo(state.step - 1) }, 'Back');
    el.next = h('button', { type: 'button', class: 'cclb-btn', onclick: () => goTo(state.step + 1) }, 'Next');
    el.mbThumb = h('canvas', { class: 'cclb-mbar__thumb', width: 72, height: 120, 'aria-hidden': 'true' });
    el.mbPeek = h('button', { type: 'button', class: 'cclb-mbar__peek', 'aria-label': 'See your label', onclick: () => root.querySelector('.cclb-preview').scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'start' }) }, el.mbThumb);
    el.mbEstimate = h('span', { class: 'cclb-mbar__estimate' });
    el.mbNext = h('button', { type: 'button', class: 'cclb-btn' }, 'Next');
    el.toast = h('div', { class: 'cclb-toast', role: 'status', 'aria-live': 'polite' });
    el.live = h('div', { class: 'cclb-sr', 'aria-live': 'polite' });
    const progressItems = STEPS.map((s, i) => h('li', null,
      h('button', { type: 'button', class: 'cclb-progress__step', onclick: () => goTo(i) },
        h('span', { class: 'cclb-progress__num', 'aria-hidden': 'true' }, i + 1), h('span', { class: 'cclb-progress__label' }, s.label))));

    root.append(h('div', { class: 'cclb-app' },
      h('header', { class: 'cclb-top' },
        h('p', { class: 'cclb-eyebrow' }, 'Custom label bags'),
        h('h1', { class: 'cclb-title' }, cfg.heading || 'Make it yours'),
        cfg.intro ? h('p', { class: 'cclb-lede' }, cfg.intro) : null,
        h('nav', { class: 'cclb-progress', 'aria-label': 'Steps' }, h('ol', null, progressItems), h('div', { class: 'cclb-progress__bar', 'aria-hidden': 'true' }, el.progressBar))),
      h('div', { class: 'cclb-main' },
        h('section', { class: 'cclb-preview', 'aria-label': 'Preview' },
          h('div', { class: 'cclb-preview__bar' }, el.sizeToggle, h('div', { class: 'cclb-seg', role: 'group', 'aria-label': 'Preview' }, el.viewBag, el.viewLabel)),
          h('div', { class: 'cclb-preview__stage' }, el.bagCanvas, el.labelCanvas),
          el.previewNote, el.estimateLine, el.saveWrap, el.checks),
        h('section', { class: 'cclb-panel', 'aria-labelledby': `cclb-panel-title-${sid}` },
          el.panelTitle, el.panels, h('div', { class: 'cclb-panel__nav' }, el.back, el.next))),
      h('div', { class: 'cclb-mbar' }, el.mbPeek, el.mbEstimate, el.mbNext),
      el.toast, el.live));

    function announce(text) { el.live.textContent = ''; requestAnimationFrame(() => { el.live.textContent = text; }); }
    let toastTimer = 0;
    function toast(text, ms = 4200) {
      el.toast.replaceChildren(text); el.toast.classList.add('is-shown');
      clearTimeout(toastTimer); toastTimer = setTimeout(() => el.toast.classList.remove('is-shown'), ms);
    }

    /* ---------- Libraries and fonts ---------- */
    const fontData = new Map();
    function ensureFont(id) {
      const f = fontById[id] || fontById['jakarta-800'];
      if (!fontData.has(f.id)) {
        fontData.set(f.id, (async () => {
          const buf = await (await fetch(A(f.file))).arrayBuffer();
          const face = new FontFace(f.family, buf);
          await face.load();
          document.fonts.add(face);
          return buf;
        })());
      }
      return fontData.get(f.id).then(() => f.family);
    }
    const ratioCache = new Map();
    // Letter heights as a share of the font size, measured once per font: capital H and lowercase o.
    function letterRatios(family) {
      if (!ratioCache.has(family)) {
        const c = document.createElement('canvas').getContext('2d');
        c.font = `100px "${family}"`;
        const m = ch => { const t = c.measureText(ch); return (t.actualBoundingBoxAscent + t.actualBoundingBoxDescent) / 100; };
        ratioCache.set(family, { cap: m('H'), x: m('o'), nWidth: c.measureText('N').width / 100 });
      }
      return ratioCache.get(family);
    }

    let fabric;
    el.panels.append(h('p', { class: 'cclb-loading' }, 'Setting up the label…'));
    try {
      await loadScript(A('cc-label-vendor-fabric.min.js'));
      fabric = window.fabric;
      const fonts = new Set(['jakarta-800', 'oswald-600']);
      STYLES.forEach(s => s.layers.forEach(l => { if (l.font) fonts.add(l.font); }));
      await Promise.all([...fonts].map(ensureFont));
    } catch (err) {
      console.error(err);
      el.panels.replaceChildren(h('p', null, 'The label builder could not load. Please refresh, or ', h('a', { href: cfg.quoteUrl || '/pages/corporate-gifting' }, 'get a quote'), ' instead.'));
      return;
    }
    el.panels.replaceChildren();

    /* ------------------------------------------------------------------
       The label: drawn off screen, then shown on the bag or up close
       ------------------------------------------------------------------ */
    const B = cfg.print.bleedIn * PT;
    const TW = cfg.print.trimIn[0] * PT, TH = cfg.print.trimIn[1] * PT;
    const CW = TW + 2 * B, CH = TH + 2 * B;
    const canvas = new fabric.StaticCanvas(null, { width: CW, height: CH, enableRetinaScaling: false, backgroundColor: '#FFFFFF', renderOnAddRemove: false });
    const sceneBox = box => ({ x: box[0] + B, y: box[1] + B, w: box[2], h: box[3] });
    const scaleBox = (b, s) => ({ x: b.x + b.w * (1 - s) / 2, y: b.y + b.h * (1 - s) / 2, w: b.w * s, h: b.h * s });
    const roleObj = role => canvas.getObjects().find(o => o.cclbRole === role);
    const fillColors = (str, colors) => String(str).replace(/\{(\w+)\}/g, (m, k) => colors[k] || m);
    const caps = (spec, s) => spec.uppercase ? String(s).toUpperCase() : String(s);
    const isActive = layer => !layer.when || ({ qr: qrOn(), noqr: !qrOn(), message: hasMessage(), nomessage: !hasMessage() })[layer.when];

    function styleColors(style = currentStyle()) {
      const c = { ...style.colors };
      c.accent = state.color || c.accent;
      c.accentInk = contrastRatio('#FFFFFF', c.accent) >= 3 ? '#FFFFFF' : c.ink;
      c.accentText = readableOn(c.accent, c.background || '#FFFFFF');
      return c;
    }
    async function svgGroup(svgText) {
      const { objects, options } = await fabric.loadSVGFromString(svgText);
      return fabric.util.groupSVGElements(objects.filter(Boolean), options);
    }
    // Fits an object into a box (scene points), or stretches it to fill the box.
    function placeIn(o, box, mode = 'fit') {
      const sx = box.w / o.width, sy = box.h / o.height;
      if (mode === 'fill') o.set({ scaleX: sx, scaleY: sy });
      else { const s = Math.min(sx, sy); o.set({ scaleX: s, scaleY: s }); }
      o.set({ left: box.x + box.w / 2, top: box.y + box.h / 2, angle: 0 });
      o.setCoords();
      return o;
    }
    let ccLogoSvg = null;
    async function ccLogoGroup() {
      if (!ccLogoSvg) ccLogoSvg = await (await fetch(A('cc-label-cactus-corn-logo.svg'))).text();
      return svgGroup(ccLogoSvg);
    }
    function textbox(text, spec, family) {
      return new fabric.Textbox(text, {
        fontFamily: family, fill: spec.fill, textAlign: spec.align || 'center', lineHeight: spec.lineHeight || 1.05,
        fontSize: spec.size || spec.maxSize || 12, width: 100, editable: false, splitByGrapheme: false, objectCaching: false
      });
    }
    // Shrinks text until it fits its box: no word wider than the box, no more lines than allowed.
    function fitText(tb, box, { max, min, lines, valign }) {
      let size = max;
      tb.set({ width: box.w, fontSize: size }); tb.initDimensions();
      const fits = () => tb.textLines.length <= lines && tb.height <= box.h + 0.5 && (tb.dynamicMinWidth || 0) <= box.w + 0.5;
      while (!fits() && size > min) { size = Math.max(min, size - 0.25); tb.set({ fontSize: size }); tb.initDimensions(); }
      tb.set({ left: box.x + box.w / 2, top: valign === 'top' ? box.y + tb.height / 2 : box.y + box.h / 2 }); tb.setCoords();
      return fits();
    }

    /* ---------- Text that follows the flavor and bag ---------- */
    function netWeightText() {
      const nw = (flavor().netWt || {})[state.productId] || {};
      const oz = String(nw.oz || '').trim(), g = String(nw.g || '').trim();
      return { text: cfg.fda.netWtFormat.replace('{oz}', oz || '__').replace('{g}', g || '__'), missing: !oz || !g };
    }
    function ingredientsText() {
      const f = flavor();
      if (!f.ingredients) return { text: '', missing: true };
      let t = 'INGREDIENTS: ' + f.ingredients.replace(/\.\s*$/, '') + '.';
      if (f.contains) t += ' CONTAINS: ' + f.contains.replace(/\.\s*$/, '') + '.';
      return { text: t, missing: false };
    }
    function layoutNetWeight(o, spec) {
      const box = sceneBox(spec.box), { text } = netWeightText(), r = letterRatios(o.fontFamily);
      // Capitals only: capital letters must meet the minimum. Any lowercase: the lowercase o must (21 CFR 101.7(h)(2)).
      const ratio = /[a-z]/.test(text) ? r.x : r.cap;
      const size = Math.ceil((cfg.fda.netWtMinTypeIn * PT / ratio) * 4) / 4;
      o.set({ text, fontSize: size, width: box.w }); o.initDimensions();
      o.set({ left: box.x + box.w / 2, top: box.y + box.h - o.height / 2 }); o.setCoords();
      o.cclbFits = (o.dynamicMinWidth || 0) <= box.w + 0.5 && o.textLines.length === 1;
    }
    const STACK_GAP = 6; // points between the flavor name and the ingredients
    function layoutIngredients(o, spec, sc) {
      const { text } = ingredientsText(), mode = cfg.fda.frontIngredients || 'fit';
      // The ingredients get all the room the flavor name leaves in the space the two boxes share.
      let box = sceneBox(spec.box), lines = spec.maxLines || 5;
      const flav = sc && sc.getObjects().find(x => x.cclbRole === 'flavorName');
      if (flav && flav.cclbSpec) {
        const fb = sceneBox(flav.cclbSpec.box), top = Math.min(fb.y, box.y), bottom = Math.max(fb.y + fb.h, box.y + box.h);
        const y = top + flav.height + STACK_GAP;
        if (bottom - y > box.h) { box = { x: box.x, y, w: box.w, h: bottom - y }; lines = 99; }
      }
      // FDA minimum: 1/16 inch letters (measured on capitals, since the line is all capitals).
      const min = Math.max(spec.minSize || 5, Math.ceil((PT / 16 / letterRatios(o.fontFamily).cap) * 4) / 4);
      const note = cfg.fda.backLabelNote || 'INGREDIENTS AND ALLERGENS ON BACK';
      o.cclbOnBack = false;
      if (mode === 'never' || !text) { o.set({ text: note }); o.cclbOnBack = true; fitText(o, box, { max: spec.size || 6.6, min, lines: 2, valign: 'top' }); return; }
      o.set({ text });
      if (!fitText(o, box, { max: spec.size || 6.6, min, lines, valign: 'top' }) && mode === 'fit') {
        o.set({ text: note }); o.cclbOnBack = true; fitText(o, box, { max: spec.size || 6.6, min, lines: 2, valign: 'top' });
      }
    }
    const layoutFlavor = (o, spec) => { o.set({ text: caps(spec, flavor().identity) }); fitText(o, sceneBox(spec.box), { max: spec.maxSize || 26, min: spec.minSize || 12, lines: spec.maxLines || 3 }); };
    const layoutMessage = (o, spec) => { o.set({ text: caps(spec, state.message.trim()) }); fitText(o, sceneBox(spec.box), { max: spec.size || 12, min: Math.min(7, spec.size || 12), lines: cfg.limits.messageMaxLines }); };
    const layoutCaption = (o, spec) => { o.set({ text: caps(spec, state.qrCaption.trim()) }); fitText(o, sceneBox(spec.box), { max: spec.size || 7.5, min: spec.minSize || 6, lines: spec.maxLines || 4 }); };
    // Food labels need the flavor to stand out: other words stay under twice its letter height.
    function enforceIdentityRule(sc) {
      const flav = sc.getObjects().find(o => o.cclbRole === 'flavorName');
      if (!flav) return;
      const limit = flav.fontSize * letterRatios(flav.fontFamily).cap * 2;
      for (const o of sc.getObjects().filter(x => x.cclbRole === 'message' || x.cclbRole === 'qrCaption')) {
        const r = letterRatios(o.fontFamily), cap = o.fontSize * (/[a-z]/.test(o.text) ? Math.max(r.cap, r.x) : r.cap);
        if (cap > limit) { o.set({ fontSize: o.fontSize * limit / cap }); o.initDimensions(); o.setCoords(); }
      }
    }
    // The ingredients go right under the flavor name, and the pair sits centered in the space their two boxes share.
    function stackFlavorAndIngredients(sc) {
      const flav = sc.getObjects().find(o => o.cclbRole === 'flavorName'), ing = sc.getObjects().find(o => o.cclbRole === 'ingredients');
      if (!flav || !ing) return;
      const fb = sceneBox(flav.cclbSpec.box), ib = sceneBox(ing.cclbSpec.box);
      const top = Math.min(fb.y, ib.y), room = Math.max(fb.y + fb.h, ib.y + ib.h) - top;
      const gap = STACK_GAP, total = flav.height + gap + ing.height;
      if (total > room) return;
      const y = top + (room - total) / 2;
      flav.set({ top: y + flav.height / 2 }); flav.setCoords();
      ing.set({ top: y + flav.height + gap + ing.height / 2 }); ing.setCoords();
    }

    /* ---------- QR code ---------- */
    function normalizeUrl(v) {
      let s = String(v || '').trim();
      if (!s) return '';
      if (!/^[a-z][a-z0-9+.-]*:/i.test(s)) s = 'https://' + s;
      try { const u = new URL(s); return /^https?:$/.test(u.protocol) && u.hostname.includes('.') ? u.href : ''; } catch (e) { return ''; }
    }
    async function qrObject(url) {
      await loadScript(A('cc-label-vendor-qrcode.js'));
      const qr = window.qrcode(0, 'M');
      qr.addData(url); qr.make();
      const n = qr.getModuleCount(), quiet = 4, size = n + quiet * 2;
      let d = '';
      for (let r = 0; r < n; r++) {
        let c = 0;
        while (c < n) {
          if (!qr.isDark(r, c)) { c++; continue; }
          let run = 1; while (c + run < n && qr.isDark(r, c + run)) run++;
          d += `M${c + quiet} ${r + quiet}h${run}v1h-${run}z`;
          c += run;
        }
      }
      const bg = new fabric.Rect({ left: size / 2, top: size / 2, width: size, height: size, fill: '#FFFFFF', strokeWidth: 0 });
      const path = new fabric.Path(d, { fill: '#000000', strokeWidth: 0 });
      path.set({ left: quiet + n / 2, top: quiet + n / 2 });
      const g = new fabric.Group([bg, path], { objectCaching: false });
      g.cclbIsQr = true; g.cclbUrl = url;
      return g;
    }
    function placeholder(box, text, fill) {
      const r = new fabric.Rect({ width: box.w, height: box.h, fill: fill || 'rgba(4,42,26,0.04)', stroke: '#7FAC41', strokeDashArray: [4, 3], strokeWidth: 1, rx: 4, ry: 4 });
      const t = new fabric.Textbox(text, { width: box.w, fontSize: Math.min(12, box.h / 4), fontFamily: fontById['jakarta-800'].family, fill: '#3B8640', textAlign: 'center' });
      const g = new fabric.Group([r, t], { objectCaching: false, excludeFromExport: true });
      g.cclbPlaceholder = true;
      return placeIn(g, box, 'fill');
    }

    /* ---------- Building a style ---------- */
    // Lays out one style on a canvas: the main label, or a style thumbnail with a copy of the logo.
    async function buildInto(sc, style, logo) {
      const colors = styleColors(style);
      sc.remove(...sc.getObjects());
      const layers = style.layers.filter(isActive);
      await Promise.all([...new Set(layers.filter(l => l.font).map(l => l.font))].map(ensureFont));
      const qrUrl = normalizeUrl(state.qrUrl);
      for (const spec of layers) {
        const box = sceneBox(spec.box);
        let o = null;
        if (spec.kind === 'rect') {
          o = placeIn(new fabric.Rect({ width: box.w, height: box.h, fill: fillColors(spec.fill, colors), strokeWidth: 0, objectCaching: false }), box, 'fill');
          o.cclbRole = spec.role || 'shape';
        } else if (spec.kind === 'art') {
          o = placeIn(await svgGroup(fillColors(lib.art[spec.art], colors)), box, 'fill');
          o.cclbRole = 'art';
        } else if (spec.role === 'ccLogo') {
          o = placeIn(await ccLogoGroup(), box, 'fit');
        } else if (spec.role === 'clientLogo') {
          o = logo ? placeIn(logo, scaleBox(box, state.logoScale), 'fit') : placeholder(box, 'Your logo here');
        } else if (spec.role === 'qr') {
          const min = cfg.limits.qrMinIn * PT, qb = Math.min(box.w, box.h) < min ? scaleBox(box, min / Math.min(box.w, box.h)) : box;
          o = qrUrl ? placeIn(await qrObject(qrUrl), qb, 'fit') : placeholder(qb, 'QR code', '#FFFFFF');
        } else if (spec.font) {
          o = textbox('', { ...spec, fill: fillColors(spec.fill, colors) }, fontById[spec.font].family);
          ({ flavorName: layoutFlavor, ingredients: layoutIngredients, netWeight: layoutNetWeight, message: layoutMessage, qrCaption: layoutCaption })[spec.role](o, spec, sc);
        }
        if (!o) continue;
        o.cclbRole = o.cclbRole || spec.role;
        o.cclbSpec = spec;
        o.set({ selectable: false, evented: false });
        sc.add(o);
      }
      enforceIdentityRule(sc);
      stackFlavorAndIngredients(sc);
      sc.renderAll();
    }
    let building = Promise.resolve();
    function rebuild() {
      building = building.then(async () => {
        await buildInto(canvas, currentStyle(), state.logoObj);
        el.labelCanvas.setAttribute('aria-label', labelDescription());
        runChecks();
        repaint();
      }).catch(err => console.error('Label builder: layout failed', err));
      return building;
    }
    function labelDescription() {
      const parts = [state.logo ? 'your logo' : 'a spot for your logo', hasMessage() ? `the message "${state.message.trim()}"` : null, flavor().identity, 'ingredients', 'the Cactus Corn logo', qrOn() ? 'a QR code' : null];
      return 'Your label: ' + parts.filter(Boolean).join(', ') + '.';
    }

    let exporting = false;
    // The label at a given resolution, trimmed to the 3 x 5 inch edge.
    function labelImage(pxPerPt = 3) {
      exporting = true;
      const full = canvas.toCanvasElement(pxPerPt);
      exporting = false;
      const out = document.createElement('canvas');
      out.width = Math.round(TW * pxPerPt); out.height = Math.round(TH * pxPerPt);
      out.getContext('2d').drawImage(full, B * pxPerPt, B * pxPerPt, out.width, out.height, 0, 0, out.width, out.height);
      return out;
    }

    /* ------------------------------------------------------------------
       Logo upload
       ------------------------------------------------------------------ */
    function fileKind(file) {
      const n = file.name.toLowerCase();
      if (/\.(ai|eps)$/.test(n)) return 'attachment';
      if (file.type === 'image/svg+xml' || n.endsWith('.svg')) return 'svg';
      if (file.type === 'application/pdf' || n.endsWith('.pdf')) return 'pdf';
      if (['image/png', 'image/jpeg'].includes(file.type) || /\.(png|jpe?g)$/.test(n)) return 'raster';
      return null;
    }
    const sanitizeSvg = text => text.replace(/<script[\s\S]*?<\/script>/gi, '').replace(/\son\w+\s*=\s*(['"]).*?\1/gi, '')
      .replace(/<image\b[^>]*(?:xlink:)?href\s*=\s*(['"])(?!data:)[^'"]*\1[^>]*\/?>/gi, '');
    function attachmentCard(name) {
      const r = new fabric.Rect({ width: 170, height: 60, fill: '#FEF8EF', stroke: '#3B8640', strokeWidth: 1, rx: 4, ry: 4 });
      const t = new fabric.Textbox(`${name}\nOur designers will place this file`, { width: 160, fontSize: 9, fontFamily: fontById['jakarta-800'].family, fill: '#042A1A', textAlign: 'center' });
      const g = new fabric.Group([r, t], { objectCaching: false, excludeFromExport: true });
      g.cclbPlaceholder = true;
      return g;
    }
    function hasTransparency(img) {
      const c = document.createElement('canvas'), s = Math.min(1, 1200 / Math.max(img.naturalWidth, img.naturalHeight));
      c.width = Math.max(1, Math.round(img.naturalWidth * s)); c.height = Math.max(1, Math.round(img.naturalHeight * s));
      const g = c.getContext('2d', { willReadFrequently: true }); g.drawImage(img, 0, 0, c.width, c.height);
      const d = g.getImageData(0, 0, c.width, c.height).data;
      for (let i = 3; i < d.length; i += 4) if (d[i] < 250) return true;
      return false;
    }
    async function renderPdfLogo(bytes) {
      const pdfjs = await import(A('cc-label-vendor-pdfjs.min.js'));
      pdfjs.GlobalWorkerOptions.workerSrc = A('cc-label-vendor-pdfjs-worker.min.js');
      const doc = await pdfjs.getDocument({ data: bytes.slice() }).promise;
      const page = await doc.getPage(1);
      const base = page.getViewport({ scale: 1 });
      const vp = page.getViewport({ scale: 1800 / Math.max(base.width, base.height) });
      const c = document.createElement('canvas'); c.width = Math.ceil(vp.width); c.height = Math.ceil(vp.height);
      await page.render({ canvasContext: c.getContext('2d'), viewport: vp, canvas: c, background: 'rgba(0,0,0,0)' }).promise;
      return c.toDataURL('image/png');
    }
    // Builds the logo object from a saved or uploaded file, and reads its colors.
    async function logoObjectFrom(logo) {
      let obj, source = null;
      if (logo.kind === 'attachment') obj = attachmentCard(logo.name);
      else if (logo.kind === 'svg') {
        obj = await svgGroup(sanitizeSvg(atob(logo.originalDataUrl.split(',')[1])));
        if (!obj || !obj.width) throw new Error('empty svg');
        source = obj.toCanvasElement({ multiplier: Math.min(4, 400 / Math.max(obj.width, obj.height)) });
      } else if (logo.kind === 'pdf') {
        logo.previewDataUrl = logo.previewDataUrl || await renderPdfLogo(dataUrlToBytes(logo.originalDataUrl));
        const img = await loadImage(logo.previewDataUrl);
        obj = new fabric.FabricImage(img);
        obj.cclbPdfLogo = true;
        source = img;
      } else {
        const img = await loadImage(logo.originalDataUrl);
        logo.pxW = img.naturalWidth; logo.pxH = img.naturalHeight;
        logo.hasAlpha = hasTransparency(img);
        obj = new fabric.FabricImage(img);
        source = img;
      }
      logo.vector = logo.kind === 'svg' || logo.kind === 'pdf';
      obj.set({ objectCaching: false });
      return { obj, colors: source ? logoColors(source) : [] };
    }
    async function handleLogoFile(file) {
      const kind = fileKind(file);
      if (!kind) { logoError(`We can't open ${file.name.split('.').pop().toUpperCase()} files. Use SVG, PDF, PNG, or JPG. AI and EPS files are welcome too.`); return; }
      if (file.size > cfg.limits.maxUploadMb * 1048576) { logoError(`That file is ${(file.size / 1048576).toFixed(1)} MB. The limit is ${cfg.limits.maxUploadMb} MB. Try a smaller PNG or a PDF.`); return; }
      logoError('');
      el.logoStatus.replaceChildren(h('p', { class: 'cclb-help' }, 'Reading your logo…'));
      try {
        const logo = { kind, name: file.name, type: file.type || '', size: file.size, originalDataUrl: await blobToDataUrl(file) };
        const { obj, colors } = await logoObjectFrom(logo);
        Object.assign(state, { logo, logoObj: obj, logoColors: colors, logoScale: 1 });
        // The label color: the logo's most-used color that reads on white, so the flavor name keeps that exact color.
        if (!state.colorPicked && colors.length) state.color = colors.find(c => contrastRatio('#FFFFFF', c) >= 3) || colors[0];
        thumbCache.clear();
        await rebuild();
        track('logo_uploaded', { file_type: kind, dpi_status: dpiStatus().level });
        renderLogoPanel(); buildColors();
        announce('Logo added.');
        if (!state.colorPicked && colors.length) toast('We matched your label color to your logo. You can change it under Style.');
        saveDraft();
      } catch (err) {
        console.error(err);
        el.logoStatus.replaceChildren();
        logoError('We couldn\u2019t read that file. Try saving it again as a PNG or PDF.');
      }
    }
    async function removeLogo() {
      Object.assign(state, { logo: null, logoObj: null, logoColors: [], logoScale: 1 });
      if (!state.colorPicked) state.color = null;
      thumbCache.clear();
      await rebuild();
      renderLogoPanel(); buildColors(); saveDraft();
    }
    // Effective resolution of a photo logo at its printed size.
    function dpiStatus() {
      const logo = state.logo, o = state.logoObj;
      if (!logo || !o) return { level: 'none' };
      if (logo.kind === 'attachment') return { level: 'info', text: 'Our design team will place this file by hand.' };
      if (logo.vector) return { level: 'good', text: 'Vector file. Prints sharp at any size.' };
      const dpi = Math.round(Math.min(logo.pxW / ((o.width * o.scaleX) / PT), logo.pxH / ((o.height * o.scaleY) / PT)));
      if (dpi >= 300) return { level: 'good', dpi, text: `Looks sharp (${dpi} dpi at this size).` };
      if (dpi >= 150) return { level: 'warn', dpi, text: `May look soft (${dpi} dpi at this size). A larger file will print better.` };
      return { level: 'bad', dpi, text: `This will print blurry (${dpi} dpi at this size). Upload a larger file or a vector file (SVG or PDF).` };
    }

    /* ------------------------------------------------------------------
       Notes under the preview
       ------------------------------------------------------------------ */
    const ICON = { good: '\u2713', warn: '!', bad: '\u2715', info: 'i' };
    const checkLine = c => h('li', { class: `cclb-check cclb-check--${c.level}` }, h('span', { class: 'cclb-check__icon', 'aria-hidden': 'true' }, ICON[c.level]), h('span', null, c.text));
    let lastChecks = [];
    function showChecks() {
      el.checks.replaceChildren(...lastChecks.filter(c => c.key !== 'netwt' || STEPS[state.step].id === 'proof').map(checkLine));
    }
    let qualityWarned = false;
    function runChecks() {
      const out = [];
      const dpi = dpiStatus();
      if (dpi.level === 'warn' || dpi.level === 'bad') out.push({ level: dpi.level, text: 'Logo: ' + dpi.text, key: 'dpi' });
      const words = [state.message, qrOn() ? state.qrCaption : ''].join(' ').toLowerCase();
      const flagged = cfg.fda.claims.filter(c => new RegExp('(^|[^a-z])' + c.replace(/[.*+?^${}()|[\]\\]/g, '\\$&').replace(/[\s-]+/g, '[\\s-]*') + '($|[^a-z])').test(words));
      if (flagged.length) out.push({ level: 'warn', text: `Our team will review this wording before we print: ${flagged.map(w => `"${w}"`).join(', ')}.`, key: 'claims' });
      if (qrOn() && !normalizeUrl(state.qrUrl)) out.push({ level: 'warn', text: 'Add the link for your QR code, or pick "No QR code."', key: 'qr' });
      const ing = roleObj('ingredients');
      if (ing && ing.cclbOnBack && cfg.fda.frontIngredients !== 'never') out.push({ level: 'info', text: 'This flavor\u2019s ingredient list is long, so it goes on the back label.', key: 'ingredients' });
      if (netWeightText().missing) out.push({ level: 'info', text: 'We fill in the net weight for your flavor and bag.', key: 'netwt' });
      const nw = roleObj('netWeight');
      if (nw && nw.cclbFits === false) out.push({ level: 'warn', text: 'The net weight line is wider than its spot. Our team will adjust the layout.', key: 'netwtfit' });
      lastChecks = out;
      showChecks();
      if (!qualityWarned && (dpi.level === 'warn' || dpi.level === 'bad')) { qualityWarned = true; track('logo_quality_warning_shown', { dpi_status: dpi.level }); }
      return out;
    }

    /* ------------------------------------------------------------------
       Preview: the label on the bag, drawn to scale, or up close
       ------------------------------------------------------------------ */
    const bagPhotos = new Map();
    function bagPhoto(url) {
      if (!bagPhotos.has(url)) bagPhotos.set(url, loadImage(url).catch(() => null));
      return bagPhotos.get(url);
    }
    // Bag sizes for people: one decimal at most, so 7.71 x 12.22 reads "7.7 x 12.2 in".
    const bagSize = p => p.faceIn.map(v => String(Math.round(v * 10) / 10)).join(' x ') + ' in';
    function bagShape(g, x, y, w, hgt) {
      const r = w * 0.035;
      g.beginPath();
      g.moveTo(x + r, y); g.lineTo(x + w - r, y); g.quadraticCurveTo(x + w, y, x + w, y + r);
      g.lineTo(x + w + w * 0.012, y + hgt * 0.92);
      g.quadraticCurveTo(x + w, y + hgt, x + w * 0.9, y + hgt);
      g.lineTo(x + w * 0.1, y + hgt);
      g.quadraticCurveTo(x, y + hgt, x - w * 0.012, y + hgt * 0.92);
      g.lineTo(x, y + r); g.quadraticCurveTo(x, y, x + r, y);
      g.closePath();
    }
    // A clear stand-up pouch with popcorn in it. Every size comes from inches, so the bags compare to scale.
    function drawStandInBag(g, x, y, bw, bh, ppi, f) {
      g.save(); g.fillStyle = 'rgba(4,42,26,0.16)'; g.filter = 'blur(16px)';
      g.beginPath(); g.ellipse(x + bw / 2, y + bh + 4, bw * 0.5, 18, 0, 0, TAU); g.fill(); g.restore();
      const fillTop = y + ppi * 1.2;
      g.save(); bagShape(g, x, y, bw, bh); g.clip();
      g.fillStyle = 'rgba(255,255,255,0.4)'; g.fillRect(x - 20, y, bw + 40, bh);
      const tex = popcornTexture(f.look || 'kettle', 1440), ts = Math.max(bw, bh) + 80;
      g.beginPath(); g.moveTo(x - 30, fillTop + 20);
      for (let i = 0; i <= 12; i++) g.lineTo(x - 30 + (bw + 60) * i / 12, fillTop + 12 * Math.sin(i * 1.7) + (i % 2 ? 8 : -4));
      g.lineTo(x + bw + 30, y + bh + 30); g.lineTo(x - 30, y + bh + 30); g.closePath();
      g.save(); g.clip(); g.drawImage(tex, x + bw / 2 - ts / 2, fillTop - 40, ts, ts); g.restore();
      g.fillStyle = 'rgba(255,255,255,0.12)'; g.fillRect(x - 20, y, bw + 40, bh);
      const edge = g.createLinearGradient(x, 0, x + bw, 0);
      edge.addColorStop(0, 'rgba(255,255,255,0.55)'); edge.addColorStop(0.06, 'rgba(255,255,255,0.08)'); edge.addColorStop(0.94, 'rgba(255,255,255,0.08)'); edge.addColorStop(1, 'rgba(255,255,255,0.5)');
      g.fillStyle = edge; g.fillRect(x - 20, y, bw + 40, bh);
      g.fillStyle = 'rgba(255,255,255,0.55)'; g.fillRect(x, y, bw, ppi * 0.4);
      g.strokeStyle = 'rgba(4,42,26,0.14)'; g.lineWidth = 2;
      for (let i = 0; i < 2; i++) { const zy = y + ppi * (0.6 + i * 0.1); g.beginPath(); g.moveTo(x + 4, zy); g.lineTo(x + bw - 4, zy); g.stroke(); }
      g.restore();
      g.save(); bagShape(g, x, y, bw, bh); g.strokeStyle = 'rgba(4,42,26,0.28)'; g.lineWidth = 2; g.stroke(); g.restore();
    }
    // The label goes on in thin strips with a slight bulge, so it sits on the pouch instead of floating.
    function drawCurvedLabel(g, label, r) {
      const strips = 48, bulge = r.h * 0.01, outline = new Path2D();
      g.save(); g.shadowColor = 'rgba(4,42,26,0.22)'; g.shadowBlur = 10; g.shadowOffsetY = 3; g.fillStyle = '#fff'; g.fillRect(r.x, r.y, r.w, r.h); g.restore();
      for (let i = 0; i < strips; i++) {
        const t0 = i / strips, curve = Math.sin((t0 + 0.5 / strips) * Math.PI);
        const sx = r.x + r.w * t0, sy = r.y - bulge * curve, sw = r.w / strips + 0.5, sh = r.h + bulge * curve * 2;
        g.drawImage(label, label.width * t0, 0, label.width / strips + 0.5, label.height, sx, sy, sw, sh);
        outline.rect(sx, sy, sw, sh);
      }
      // Darker toward the sides, as if the label wraps the bag. Only on the label itself, never on what's around it.
      g.save(); g.globalCompositeOperation = 'multiply';
      const shade = g.createLinearGradient(r.x, 0, r.x + r.w, 0);
      shade.addColorStop(0, 'rgba(204,200,192,1)'); shade.addColorStop(0.16, 'rgba(255,255,255,1)'); shade.addColorStop(0.84, 'rgba(255,255,255,1)'); shade.addColorStop(1, 'rgba(208,204,196,1)');
      g.fillStyle = shade; g.fill(outline);
      g.restore();
    }
    async function drawBag(c, opts = {}) {
      const g = c.getContext('2d'), W = c.width, H = c.height;
      const p = opts.product || product(), f = flavor();
      const label = labelImage(4);
      // A real bag photo for this size when there is one (shot on white), otherwise a drawn bag on cream.
      const photo = p.photo ? await bagPhoto(p.photo) : null;
      g.clearRect(0, 0, W, H);
      if (photo) g.fillStyle = '#FFFFFF';
      else { const bg = g.createLinearGradient(0, 0, 0, H); bg.addColorStop(0, '#FFFDF9'); bg.addColorStop(1, '#F3E6D2'); g.fillStyle = bg; }
      g.fillRect(0, 0, W, H);
      // One scale for every bag, set by the tallest, so the snack bag really looks smaller.
      const tallest = Math.max(...cfg.products.map(x => x.faceIn[1])), widest = Math.max(...cfg.products.map(x => x.faceIn[0]));
      const ppi = Math.min((H * 0.8) / tallest, (W * 0.78) / widest);
      const bw = p.faceIn[0] * ppi, bh = p.faceIn[1] * ppi;
      const x = (W - bw) / 2, ground = H * 0.88, y = ground - bh;
      // On screen, a dashed outline of each bigger bag shows how the sizes compare.
      if (opts.ghosts !== false) {
        g.save(); g.setLineDash([10, 8]); g.lineWidth = 2; g.strokeStyle = 'rgba(4,42,26,0.28)'; g.fillStyle = 'rgba(4,42,26,0.5)';
        g.font = `${Math.round(W * 0.022)}px "CCLB Jakarta 800", sans-serif`; g.textAlign = 'center'; g.textBaseline = 'bottom';
        for (const other of cfg.products) {
          const ow = other.faceIn[0] * ppi, oh = other.faceIn[1] * ppi, ox = (W - ow) / 2, oy = ground - oh;
          if (other.id === p.id || oh <= bh) continue;
          bagShape(g, ox, oy, ow, oh); g.stroke();
          g.fillText(`${other.name} outline, for scale`, W / 2, oy - 8);
        }
        g.restore();
      }
      if (photo) {
        // The photo is cropped to the bag's edges, so it fills the bag's outline at this scale.
        g.save(); g.fillStyle = 'rgba(4,42,26,0.14)'; g.filter = 'blur(14px)';
        g.beginPath(); g.ellipse(x + bw / 2, y + bh, bw * 0.48, 14, 0, 0, TAU); g.fill(); g.restore();
        const s = Math.max(bw / photo.naturalWidth, bh / photo.naturalHeight);
        g.drawImage(photo, x + (bw - photo.naturalWidth * s) / 2, y + (bh - photo.naturalHeight * s) / 2, photo.naturalWidth * s, photo.naturalHeight * s);
      } else drawStandInBag(g, x, y, bw, bh, ppi, f);
      // On a photo the buyer's label goes right over the sample label in it, drawn 2.5% larger so no edge of that one shows.
      const k = photo ? 1.025 : 1, lw = (TW / PT) * ppi, lh = (TH / PT) * ppi;
      const lcx = x + bw / 2, lcy = y + clamp(p.labelTopIn, 0, p.faceIn[1] - TH / PT) * ppi + lh / 2;
      drawCurvedLabel(g, label, { x: lcx - lw * k / 2, y: lcy - lh * k / 2, w: lw * k, h: lh * k });
      g.save(); g.globalCompositeOperation = 'screen';
      const sheen = g.createLinearGradient(x - bw * 0.3, 0, x + bw * 1.3, H);
      sheen.addColorStop(0, 'rgba(255,255,255,0)'); sheen.addColorStop(0.44, 'rgba(255,255,255,0)'); sheen.addColorStop(0.5, 'rgba(255,255,255,0.2)'); sheen.addColorStop(0.56, 'rgba(255,255,255,0)'); sheen.addColorStop(1, 'rgba(255,255,255,0)');
      g.fillStyle = sheen; g.fillRect(0, 0, W, H); g.restore();
      g.fillStyle = 'rgba(4,42,26,0.75)';
      g.font = `${Math.round(W * 0.026)}px "CCLB Jakarta 800", sans-serif`;
      g.textAlign = 'center'; g.textBaseline = 'top';
      g.fillText(`${p.name}: about ${bagSize(p)}, with a 3 x 5 in label`, W / 2, ground + H * 0.035);
    }
    function paintCloseUp(c = el.labelCanvas) {
      const g = c.getContext('2d');
      g.clearRect(0, 0, c.width, c.height);
      g.drawImage(labelImage(c.width / TW), 0, 0, c.width, c.height);
    }
    let paintTimer = 0, bagViewed = false;
    function repaint() {
      clearTimeout(paintTimer);
      paintTimer = setTimeout(async () => {
        if (state.view === 'bag') {
          await drawBag(el.bagCanvas);
          if (!bagViewed && state.logo) { bagViewed = true; track('bag_preview_viewed'); }
        } else paintCloseUp();
        if (window.innerWidth < 960) paintCloseUp(el.mbThumb);
        el.previewNote.textContent = state.view !== 'bag' ? '' : product().photo ? 'Our real bags, shown to scale.' : 'Bag drawn to scale. Your proof shows the real thing.';
      }, reduceMotion ? 0 : 60);
    }
    function setView(v) {
      state.view = v;
      el.viewBag.setAttribute('aria-pressed', String(v === 'bag'));
      el.viewLabel.setAttribute('aria-pressed', String(v === 'label'));
      el.bagCanvas.hidden = v !== 'bag';
      el.labelCanvas.hidden = v !== 'label';
      repaint();
    }
    function renderSizeToggle() {
      el.sizeToggle.replaceChildren(...cfg.products.map(p => h('button', { type: 'button', 'aria-pressed': String(p.id === state.productId), onclick: () => setProduct(p.id) }, p.name)));
    }
    async function downloadMockup() {
      const c = document.createElement('canvas'); c.width = 1280; c.height = 1600;
      await drawBag(c, { ghosts: false });
      const a = h('a', { href: c.toDataURL('image/png'), download: `cactus-corn-label-${state.designId || 'preview'}.png` });
      document.body.append(a); a.click(); a.remove();
      track('mockup_downloaded');
    }

    /* ------------------------------------------------------------------
       Pricing
       ------------------------------------------------------------------ */
    const pricingTable = (p = product()) => cfg.pricing.tables[p.pricingId] || [];
    function tierFor(qty) { let t = null; for (const row of pricingTable()) if (qty >= row.min) t = row; return t; }
    function estimate(qty = state.qty) {
      if (qty < cfg.pricing.minQty) return null;
      const t = tierFor(qty);
      if (!t) return null;
      return { unit: t.cents, total: Number.isFinite(t.cents) ? t.cents * qty + cfg.pricing.setupCents : NaN, tier: t };
    }
    const fromPrice = p => { const c = pricingTable(p).map(r => r.cents).filter(Number.isFinite); return c.length ? Math.min(...c) : NaN; };

    /* ------------------------------------------------------------------
       Steps
       ------------------------------------------------------------------ */
    const panels = {};
    const field = (label, control, help, id) => h('div', { class: 'cclb-field' }, h('label', { class: 'cclb-label', for: id }, label), control, help ? h('p', { class: 'cclb-help', id: id + '-help' }, help) : null);
    // A small drawing of each bag with its label, all at one scale.
    function bagIcon(p) {
      const tallest = Math.max(...cfg.products.map(x => x.faceIn[1])), s = 66 / tallest;
      const w = p.faceIn[0] * s, hh = p.faceIn[1] * s, x = (60 - w) / 2, y = 70 - hh;
      const lx = x + (w - 3 * s) / 2, ly = y + clamp(p.labelTopIn, 0, p.faceIn[1] - 5) * s;
      const span = h('span', { class: 'cclb-bagicon', 'aria-hidden': 'true' });
      span.innerHTML = `<svg viewBox="0 0 60 72" width="60" height="72"><rect x="${x}" y="${y}" width="${w}" height="${hh}" rx="2" fill="#F3E4BD" stroke="#9AA39E"/><line x1="${x + 1}" x2="${x + w - 1}" y1="${y + 4}" y2="${y + 4}" stroke="#9AA39E"/><rect x="${lx}" y="${ly}" width="${3 * s}" height="${5 * s}" fill="#fff" stroke="#3B8640"/></svg>`;
      return span;
    }

    // 1. Bag
    panels.bag = (() => {
      const wrap = h('div', { class: 'cclb-cards cclb-cards--2', role: 'radiogroup', 'aria-label': 'Bag size' });
      cfg.products.forEach(p => {
        const input = h('input', { type: 'radio', name: `cclb-product-${sid}`, value: p.id, class: 'cclb-sr', checked: state.productId === p.id, onchange: () => setProduct(p.id) });
        wrap.append(h('label', { class: 'cclb-card cclb-card--bag' + (state.productId === p.id ? ' is-checked' : ''), dataset: { product: p.id } }, input, bagIcon(p),
          h('span', null,
            h('span', { class: 'cclb-card__name' }, p.name),
            h('span', { class: 'cclb-card__meta' }, `${p.blurb}. About ${bagSize(p)}.`),
            h('span', { class: 'cclb-card__price' }, Number.isFinite(fromPrice(p)) ? `From ${money(fromPrice(p))} a bag` : ''))));
      });
      return h('div', { class: 'cclb-step' }, wrap,
        h('p', { class: 'cclb-help' }, `Your label is 3 x 5 inches on either bag. Minimum order is ${cfg.pricing.minQty} bags.` + (cfg.pricing.leadTimeText ? ` Ready in ${cfg.pricing.leadTimeText} after you approve your proof.` : '') + (cfg.pricing.rushText ? ` ${cfg.pricing.rushText}` : '')));
    })();

    // 2. Logo
    el.logoInput = h('input', { type: 'file', class: 'cclb-sr', id: `cclb-logo-${sid}`, accept: '.svg,.pdf,.png,.jpg,.jpeg,.ai,.eps,image/svg+xml,application/pdf,image/png,image/jpeg', onchange: e => { const f = e.target.files[0]; if (f) handleLogoFile(f); e.target.value = ''; } });
    el.logoStatus = h('div', { class: 'cclb-logo-status', 'aria-live': 'polite' });
    el.logoError = h('p', { class: 'cclb-error', role: 'alert', hidden: true });
    el.logoQuality = h('div');
    const drop = h('label', { class: 'cclb-drop', for: `cclb-logo-${sid}` },
      h('span', { class: 'cclb-drop__title' }, 'Drop in your logo'),
      h('span', { class: 'cclb-drop__text' }, `PNG, JPG, SVG, or PDF up to ${cfg.limits.maxUploadMb} MB. A PNG with a see-through background or a vector file looks best.`),
      h('span', { class: 'cclb-btn cclb-btn--small' }, 'Choose a file'));
    ['dragenter', 'dragover'].forEach(ev => drop.addEventListener(ev, e => { e.preventDefault(); drop.classList.add('is-over'); }));
    ['dragleave', 'drop'].forEach(ev => drop.addEventListener(ev, e => { e.preventDefault(); drop.classList.remove('is-over'); }));
    drop.addEventListener('drop', e => { const f = e.dataTransfer.files[0]; if (f) handleLogoFile(f); });
    panels.logo = h('div', { class: 'cclb-step' }, el.logoInput, drop, el.logoError, el.logoStatus,
      h('p', { class: 'cclb-help' }, 'Only have an AI or EPS file? Upload it anyway. Our designers will place it on your proof.'),
      h('p', { class: 'cclb-help cclb-help--skip' }, 'No logo handy? Skip this step and keep going. Send it with your proof request or reply to the proof email, and we will place it for you.'));
    function logoError(text) { el.logoError.textContent = text; el.logoError.hidden = !text; }
    function updateLogoQuality() {
      const d = dpiStatus(), level = d.level === 'info' ? 'info' : d.level;
      el.logoQuality.replaceChildren(d.text ? h('p', { class: `cclb-check cclb-check--${level}` }, h('span', { class: 'cclb-check__icon', 'aria-hidden': 'true' }, ICON[level]), h('span', null, d.text)) : '');
    }
    function renderLogoPanel() {
      const logo = state.logo;
      drop.querySelector('.cclb-drop__title').textContent = logo ? 'Replace your logo' : 'Drop in your logo';
      if (!logo) { el.logoStatus.replaceChildren(); return; }
      const size = h('input', { type: 'range', id: `cclb-logo-size-${sid}`, min: 60, max: 100, step: 5, value: Math.round(state.logoScale * 100) });
      size.addEventListener('input', debounce(async () => { state.logoScale = num(size.value, 100) / 100; thumbCache.clear(); await rebuild(); updateLogoQuality(); saveDraft(); }, 80));
      updateLogoQuality();
      el.logoStatus.replaceChildren(
        h('p', { class: 'cclb-logo-file' }, logo.name), el.logoQuality,
        logo.kind !== 'attachment' ? field('Logo size', size, null, `cclb-logo-size-${sid}`) : null,
        h('div', { class: 'cclb-actions' }, h('button', { type: 'button', class: 'cclb-chip cclb-chip--danger', onclick: removeLogo }, 'Remove logo')));
    }

    // 3. Flavor
    el.flavorGrid = h('div', { class: 'cclb-flavors', role: 'radiogroup', 'aria-label': 'Flavor' });
    panels.flavor = h('div', { class: 'cclb-step' }, el.flavorGrid,
      h('p', { class: 'cclb-help' }, 'The flavor name and ingredients fill in on your label.'));
    function renderFlavors() {
      el.flavorGrid.replaceChildren(...FLAVORS.map(f => {
        const sw = h('canvas', { class: 'cclb-flavor__swatch', width: 120, height: 120, 'aria-hidden': 'true' });
        requestAnimationFrame(() => paintSwatch(sw, f.look || 'kettle'));
        const input = h('input', { type: 'radio', name: `cclb-flavor-${sid}`, class: 'cclb-sr', value: f.id, checked: state.flavorId === f.id, onchange: () => setFlavor(f.id) });
        return h('label', { class: 'cclb-flavor' + (state.flavorId === f.id ? ' is-checked' : '') }, input, sw,
          h('span', { class: 'cclb-flavor__name' }, f.name), f.season ? h('span', { class: 'cclb-badge' }, 'Seasonal') : null);
      }));
    }

    // 4. Style: look, color, and an optional message
    el.styleCards = h('div', { class: 'cclb-styles', role: 'radiogroup', 'aria-label': 'Style' });
    el.colors = h('div', { class: 'cclb-colors' });
    el.msg = h('textarea', { id: `cclb-msg-${sid}`, rows: 2, maxlength: cfg.limits.messageMaxChars, placeholder: 'Happy holidays from our family to yours!', 'aria-describedby': `cclb-msg-${sid}-help` });
    el.msgCount = h('span', { class: 'cclb-count' });
    el.msgWarn = h('p', { class: 'cclb-help cclb-help--warn', hidden: true });
    panels.style = h('div', { class: 'cclb-step' }, el.styleCards,
      h('h3', { class: 'cclb-h3' }, 'Label color'), el.colors,
      field('Add a message (optional)', h('div', { class: 'cclb-with-count' }, el.msg, el.msgCount), `Up to ${plural(cfg.limits.messageMaxLines, 'line', 'lines')}. Leave it empty for a bigger logo.`, `cclb-msg-${sid}`),
      el.msgWarn);
    const rebuildSoon = debounce(async () => { await rebuild(); syncWarnings(); refreshThumbs(); saveDraft(); }, 200);
    const trackMessage = debounce(() => track('text_edited', { field: 'message' }), 1500);
    el.msg.addEventListener('input', () => {
      const lines = el.msg.value.split('\n');
      if (lines.length > cfg.limits.messageMaxLines) el.msg.value = lines.slice(0, cfg.limits.messageMaxLines).join('\n');
      state.message = el.msg.value;
      el.msgCount.textContent = `${el.msg.value.length}/${cfg.limits.messageMaxChars}`;
      thumbCache.clear();
      rebuildSoon(); trackMessage();
    });
    function syncWarnings() {
      const claims = lastChecks.find(c => c.key === 'claims');
      el.msgWarn.hidden = !claims; el.msgWarn.textContent = claims ? claims.text : '';
      el.qrWarn.hidden = !claims || !qrOn(); el.qrWarn.textContent = claims ? claims.text : '';
    }
    const thumbCache = new Map();
    async function styleThumb(style) {
      const sc = new fabric.StaticCanvas(null, { width: CW, height: CH, enableRetinaScaling: false, renderOnAddRemove: false });
      const logo = state.logoObj ? await state.logoObj.clone() : null;
      await buildInto(sc, style, logo);
      const url = sc.toDataURL({ format: 'png', multiplier: 0.55, left: B, top: B, width: TW, height: TH });
      sc.dispose();
      return url;
    }
    function buildStyleCards() {
      el.styleCards.replaceChildren(...STYLES.map(s => {
        const input = h('input', { type: 'radio', name: `cclb-style-${sid}`, class: 'cclb-sr', value: s.id, checked: state.styleId === s.id, onchange: () => setStyle(s.id) });
        return h('label', { class: 'cclb-style' + (state.styleId === s.id ? ' is-checked' : ''), dataset: { style: s.id } }, input,
          h('span', { class: 'cclb-style__thumb' }, h('img', { alt: '', width: 119, height: 198 })),
          h('span', { class: 'cclb-style__name' }, s.name), h('span', { class: 'cclb-style__blurb' }, s.blurb));
      }));
    }
    // Style thumbnails show the buyer's own logo, flavor, and color. They redraw only on the Style step.
    let thumbRun = 0;
    async function refreshThumbs() {
      if (STEPS[state.step].id !== 'style') return;
      const run = ++thumbRun;
      for (const s of STYLES) {
        if (!thumbCache.has(s.id)) {
          const url = await styleThumb(s);
          if (run !== thumbRun) return;
          thumbCache.set(s.id, url);
        }
        const img = el.styleCards.querySelector(`[data-style="${s.id}"] img`);
        if (img && img.src !== thumbCache.get(s.id)) img.src = thumbCache.get(s.id);
      }
    }
    function buildColors() {
      const swatch = (hex, name) => h('button', { type: 'button', class: 'cclb-swatch', dataset: { hex: hex.toUpperCase() }, style: { background: hex }, title: `${name} ${hex}`, 'aria-label': `${name} ${hex}`, 'aria-pressed': 'false', onclick: () => setColor(hex.toUpperCase(), true) });
      const groups = [];
      if (state.logoColors.length) groups.push(h('div', { class: 'cclb-swatch-group' }, h('span', { class: 'cclb-help' }, 'From your logo'), h('div', { class: 'cclb-swatches' }, state.logoColors.map(c => swatch(c, 'Logo color')))));
      groups.push(h('div', { class: 'cclb-swatch-group' }, h('span', { class: 'cclb-help' }, state.logoColors.length ? 'More colors' : 'Colors'), h('div', { class: 'cclb-swatches' }, (lib.palette || []).map(c => swatch(c.hex, c.name)))));
      el.picker = h('input', { type: 'color', class: 'cclb-picker', 'aria-label': 'Pick any color' });
      el.picker.addEventListener('input', debounce(() => setColor(el.picker.value.toUpperCase(), true), 120));
      el.hex = h('input', { type: 'text', maxlength: 7, spellcheck: 'false', class: 'cclb-hex', 'aria-label': 'Color code' });
      el.hex.addEventListener('change', () => { const v = normHex(el.hex.value); if (v) setColor(v, true); else refreshColorSelection(); });
      groups.push(h('div', { class: 'cclb-color__row' }, el.picker, el.hex, h('span', { class: 'cclb-help' }, 'Or any color')));
      el.colors.replaceChildren(...groups);
      refreshColorSelection();
    }
    function refreshColorSelection() {
      const current = styleColors().accent.toUpperCase();
      el.colors.querySelectorAll('.cclb-swatch').forEach(b => { const on = b.dataset.hex === current; b.classList.toggle('is-on', on); b.setAttribute('aria-pressed', String(on)); });
      if (el.picker && document.activeElement !== el.picker) el.picker.value = current.toLowerCase();
      if (el.hex && document.activeElement !== el.hex) el.hex.value = current;
    }

    // 5. QR code
    el.qrYes = h('button', { type: 'button', class: 'cclb-choice', 'aria-pressed': 'false', onclick: () => setQrWanted(true) },
      h('span', { class: 'cclb-choice__title' }, 'Yes, add a QR code'), h('span', { class: 'cclb-choice__text' }, 'Send people to your website, a landing page, or a coupon.'));
    el.qrNo = h('button', { type: 'button', class: 'cclb-choice', 'aria-pressed': 'false', onclick: () => setQrWanted(false) },
      h('span', { class: 'cclb-choice__title' }, 'No QR code'), h('span', { class: 'cclb-choice__text' }, 'Keep the label simple.'));
    el.qrUrl = h('input', { type: 'url', id: `cclb-qr-${sid}`, inputmode: 'url', placeholder: 'yourcompany.com/welcome', autocomplete: 'url' });
    el.qrCaption = h('input', { type: 'text', id: `cclb-qrcap-${sid}`, maxlength: 40, value: state.qrCaption });
    el.qrError = h('p', { class: 'cclb-error', role: 'alert', hidden: true });
    el.qrWarn = h('p', { class: 'cclb-help cclb-help--warn', hidden: true });
    el.qrFields = h('div', { class: 'cclb-step', hidden: true },
      field('Where should the QR code go?', el.qrUrl, 'Paste the web address. Test-scan your proof before you approve it.', `cclb-qr-${sid}`), el.qrError,
      field('Words next to the code (optional)', el.qrCaption, 'For example "Scan for 15% off" or "Visit us."', `cclb-qrcap-${sid}`), el.qrWarn);
    panels.qr = h('div', { class: 'cclb-step' }, h('div', { class: 'cclb-choices', role: 'group', 'aria-label': 'QR code' }, el.qrYes, el.qrNo), el.qrFields);
    el.qrUrl.addEventListener('input', debounce(async () => { state.qrUrl = el.qrUrl.value; updateQrError(); await rebuild(); syncWarnings(); saveDraft(); }, 400));
    el.qrCaption.addEventListener('input', debounce(async () => { state.qrCaption = el.qrCaption.value; await rebuild(); syncWarnings(); saveDraft(); }, 300));
    function updateQrError() {
      const bad = qrOn() && state.qrUrl.trim() && !normalizeUrl(state.qrUrl);
      el.qrError.hidden = !bad;
      el.qrError.textContent = bad ? 'That link doesn\u2019t look right. Try something like yourcompany.com.' : '';
    }
    function syncQrChoice() {
      el.qrYes.setAttribute('aria-pressed', String(state.qrWanted === true));
      el.qrNo.setAttribute('aria-pressed', String(state.qrWanted === false));
      el.qrFields.hidden = state.qrWanted !== true;
    }
    async function setQrWanted(v) {
      state.qrWanted = v;
      syncQrChoice();
      thumbCache.clear();
      await rebuild();
      if (v) el.qrUrl.focus();
      updateNav(); saveDraft();
      track('qr_chosen', { wanted: v });
    }

    // 6. Proof: quantity and the request form
    let proofForm = null;
    panels.proof = (() => {
      const f = {};
      const id = n => `cclb-f-${n}-${sid}`;
      const input = (n, attrs) => (f[n] = h('input', { id: id(n), name: n, ...attrs }));
      const err = n => (f[n + 'Err'] = h('p', { class: 'cclb-error', id: id(n) + '-err', hidden: true }));
      const req = h('span', { class: 'cclb-req' }, ' (required)');
      f.qty = input('qty', { type: 'number', min: cfg.pricing.minQty, step: 1, inputmode: 'numeric', value: state.qty, required: true });
      f.price = h('div', { class: 'cclb-price', 'aria-live': 'polite' });
      f.chips = h('div', { class: 'cclb-actions' }, [cfg.pricing.minQty, 100, 200, 400].filter((v, i, a) => a.indexOf(v) === i && v >= cfg.pricing.minQty).map(v => h('button', { type: 'button', class: 'cclb-chip', onclick: () => setQty(v) }, `${v} bags`)));
      f.date = input('date', { type: 'date', min: new Date().toISOString().slice(0, 10) });
      f.dateWarn = h('p', { class: 'cclb-help cclb-help--warn', hidden: true });
      const minDate = new Date(Date.now() + cfg.pricing.leadTimeDays * 864e5).toISOString().slice(0, 10);
      f.date.addEventListener('change', () => {
        const tight = f.date.value && f.date.value < minDate;
        f.dateWarn.hidden = !tight;
        f.dateWarn.textContent = tight ? `That's sooner than our usual ${cfg.pricing.leadTimeText || cfg.pricing.leadTimeDays + ' days'}. Send it anyway and we'll tell you with the proof if we can make it.` : '';
      });
      f.qty.addEventListener('change', () => setQty(Math.round(num(f.qty.value, cfg.pricing.minQty))));
      f.type = h('select', { id: id('type'), name: 'order_type' }, [['corporate', 'Corporate or client gift'], ['event', 'Event, conference, or trade show'], ['fundraiser', 'Fundraiser'], ['team', 'School or sports team'], ['wedding', 'Wedding or party'], ['personal', 'Personal gift'], ['other', 'Something else']].map(([v, l]) => h('option', { value: v }, l)));
      f.notes = h('textarea', { id: id('notes'), name: 'notes', rows: 3, maxlength: 1000 });
      f.consent = h('input', { type: 'checkbox', id: id('consent'), name: 'consent' });
      f.hp = h('input', { type: 'text', name: 'cclb_hp', tabindex: '-1', autocomplete: 'off' });
      f.status = h('div', { class: 'cclb-submit-status', role: 'status', 'aria-live': 'polite' });
      f.submit = h('button', { type: 'submit', class: 'cclb-btn cclb-btn--big' }, 'Get my free proof');
      f.cart = h('button', { type: 'button', class: 'cclb-link', hidden: true, onclick: addToCart }, 'Order now instead');
      const consentBlock = cfg.lead.subscribeMode === 'always'
        ? h('p', { class: 'cclb-help' }, 'We will email you about your proof and send occasional gifting ideas. Unsubscribe any time.')
        : h('label', { class: 'cclb-switch' }, f.consent, h('span', null, 'Send me Cactus Corn news and offers.'));
      const form = h('form', { class: 'cclb-form', novalidate: true },
        h('div', { class: 'cclb-field' }, h('label', { class: 'cclb-label', for: id('qty') }, 'How many bags?', req),
          h('div', { class: 'cclb-qty' }, h('button', { type: 'button', 'aria-label': 'Fewer bags', onclick: () => setQty(state.qty - 10) }, '\u2212'), f.qty, h('button', { type: 'button', 'aria-label': 'More bags', onclick: () => setQty(state.qty + 10) }, '+')),
          err('qty')),
        f.chips, f.price,
        h('div', { class: 'cclb-trust' },
          h('p', { class: 'cclb-trust__line' }, h('strong', null, 'Since 1998 at Chase Field.'), ' 25,000+ bags sold at State Farm Stadium in the 2025 season. Popped and labeled in Phoenix.'),
          h('ol', { class: 'cclb-trust__steps' },
            h('li', null, `We email your proof within ${cfg.pricing.proofTurnaround}.`),
            h('li', null, 'You approve it, or ask for changes.'),
            depositOn() ? h('li', null, `A ${depositPercent()}% deposit starts production. The rest is due before your order ships.`) : null,
            h('li', null, 'We pop, label, and pack. Nothing prints until you say so.'))),
        h('div', { class: 'cclb-row' },
          h('div', { class: 'cclb-field' }, h('label', { class: 'cclb-label', for: id('first') }, 'First name', req), input('first', { type: 'text', autocomplete: 'given-name', required: true }), err('first')),
          h('div', { class: 'cclb-field' }, h('label', { class: 'cclb-label', for: id('last') }, 'Last name'), input('last', { type: 'text', autocomplete: 'family-name' }))),
        h('div', { class: 'cclb-field' }, h('label', { class: 'cclb-label', for: id('email') }, 'Work email', req), input('email', { type: 'email', autocomplete: 'email', required: true }), err('email')),
        h('div', { class: 'cclb-field' }, h('label', { class: 'cclb-label', for: id('company') }, 'Company, team, or occasion'), input('company', { type: 'text', autocomplete: 'organization', placeholder: 'Acme Realty, Mesa FC U12, or the Garcia wedding' }), err('company')),
        h('div', { class: 'cclb-row' },
          h('div', { class: 'cclb-field' }, h('label', { class: 'cclb-label', for: id('phone') }, 'Phone'), input('phone', { type: 'tel', autocomplete: 'tel' }), err('phone')),
          h('div', { class: 'cclb-field' }, h('label', { class: 'cclb-label', for: id('date') }, 'Need them by'), f.date)),
        f.dateWarn,
        h('div', { class: 'cclb-field' }, h('label', { class: 'cclb-label', for: id('type') }, 'What\u2019s it for?'), f.type),
        h('div', { class: 'cclb-field' }, h('label', { class: 'cclb-label', for: id('notes') }, 'Anything else?'), f.notes),
        h('div', { class: 'cclb-hp', 'aria-hidden': 'true' }, h('label', null, 'Leave this empty', f.hp)),
        consentBlock,
        h('p', { class: 'cclb-help' }, `Your proof is free. A real person sends it within ${cfg.pricing.proofTurnaround}, and nothing prints until you approve it.`),
        f.submit, f.cart, f.status);
      form.addEventListener('submit', e => { e.preventDefault(); submitProof(); });
      let started = false;
      form.addEventListener('focusin', () => { if (!started) { started = true; track('proof_form_started'); } });
      f.form = form;
      proofForm = f;
      return h('div', { class: 'cclb-step' }, form);
    })();

    STEPS.forEach((s, i) => { panels[s.id].hidden = i !== state.step; el.panels.append(panels[s.id]); });

    function setQty(n) {
      state.qty = clamp(Math.round(n) || cfg.pricing.minQty, 1, 100000);
      proofForm.qty.value = state.qty;
      renderPrice(); updateCartButton(); trackQty(); saveDraft();
    }
    const trackQty = debounce(() => { const e = estimate(); track('quantity_selected', { quantity: state.qty, tier: e ? e.tier.min : null }); }, 800);
    function renderPrice() {
      const e = estimate(), p = product(), box = proofForm.price;
      const lead = [cfg.pricing.leadTimeText ? `Ready in ${cfg.pricing.leadTimeText} after you approve your proof.` : '', cfg.pricing.rushText].filter(Boolean).join(' ');
      if (state.qty < cfg.pricing.minQty) box.replaceChildren(h('p', { class: 'cclb-price__min' }, `The minimum is ${cfg.pricing.minQty} bags.`), h('p', { class: 'cclb-help' }, lead));
      else if (e) box.replaceChildren(
        h('p', { class: 'cclb-price__line' }, h('span', null, `${p.name}, ${state.qty.toLocaleString()} bags`), h('strong', null, `${money(e.unit)} a bag`)),
        h('p', { class: 'cclb-price__total' }, h('span', null, 'Estimated total'), h('strong', null, money(e.total))),
        h('p', { class: 'cclb-help' }, ['Estimate. Your final quote comes with your proof.', cfg.pricing.setupCents > 0 ? `Includes a ${money(cfg.pricing.setupCents)} setup fee.` : 'Label, printing, and packing included.', cfg.shippingText || ''].filter(Boolean).join(' ')),
        h('p', { class: 'cclb-help' }, lead));
      el.mbEstimate.replaceChildren(...(e && Number.isFinite(e.total)
        ? [h('strong', null, `About $${Math.round(e.total / 100).toLocaleString('en-US')}`), h('span', null, `${state.qty.toLocaleString()} ${p.name.toLowerCase()}s`)]
        : [h('span', null, `Minimum ${cfg.pricing.minQty} bags`)]));
      el.estimateLine.replaceChildren(...(e && Number.isFinite(e.total)
        ? [h('strong', null, `${state.qty.toLocaleString()} ${p.name.toLowerCase()}s, about $${Math.round(e.total / 100).toLocaleString('en-US')}`), h('span', null, ` at ${money(e.unit)} a bag${cfg.shippingText ? ', plus shipping' : ''}. Change the count on the last step.`)]
        : [h('span', null, `Minimum ${cfg.pricing.minQty} bags.`)]));
    }
    // "Email me this design": a link that rebuilds the design (everything but the logo file) on any device,
    // sent through Klaviyo so a flow can follow up. On this device the saved draft brings the logo back too.
    function resumeLink() {
      const snap = designSnapshot();
      delete snap.logo;
      const packed = btoa(unescape(encodeURIComponent(JSON.stringify(snap)))).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
      return `${location.origin}${location.pathname}?d=${packed}`;
    }
    function unpackDesign(packed) {
      try {
        const b64 = packed.replace(/-/g, '+').replace(/_/g, '/');
        const snap = JSON.parse(decodeURIComponent(escape(atob(b64 + '==='.slice((b64.length + 3) % 4)))));
        return snap && snap.v === 2 ? snap : null;
      } catch (e) { return null; }
    }
    async function copyDesignLink() {
      const link = resumeLink();
      el.saveLink.value = link;
      let copied = false;
      try { if (navigator.clipboard && window.isSecureContext) { await navigator.clipboard.writeText(link); copied = true; } } catch (e) { /* clipboard blocked */ }
      if (!copied) { el.saveLink.focus(); el.saveLink.select(); try { copied = document.execCommand('copy'); } catch (e) { copied = false; } }
      el.saveStatus.textContent = copied ? 'Copied. Paste the link anywhere to come back to this design.' : 'Select the link above and copy it.';
      track('design_link_copied', { quantity: state.qty, copied });
    }
    let saveBusy = false;
    async function emailDesign() {
      const email = el.saveEmail.value.trim();
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(email)) { el.saveStatus.textContent = 'Add an email like you@company.com.'; el.saveEmail.focus(); return; }
      if (saveBusy) return;
      saveBusy = true;
      el.saveStatus.textContent = 'Saving\u2026';
      state.designId = state.designId || newDesignId();
      const e = estimate(), p = product(), link = resumeLink();
      const props = {
        design_id: state.designId, resume_url: link, bag: p.name, flavor: flavor().name, style_id: state.styleId, quantity: state.qty,
        est_total: e && Number.isFinite(e.total) ? (e.total / 100).toFixed(2) : '', est_price_per_bag: e && Number.isFinite(e.unit) ? (e.unit / 100).toFixed(2) : '',
        has_logo: !!state.logo, step: STEPS[state.step].id, page_url: location.href.split('#')[0], ...firstTouch()
      };
      let sent = false;
      try {
        const key = cfg.lead.klaviyoPublicKey;
        if (key && !(cfg.lead.testMode && !testEmail(email))) {
          const headers = { 'Content-Type': 'application/json', revision: '2024-10-15' };
          const profile = { data: { type: 'profile', attributes: { email, properties: { lead_source: 'Label Builder', label_design_resume_url: link, label_design_summary: `${state.qty} ${p.name.toLowerCase()}s of ${flavor().name}` } } } };
          const ev = { data: { type: 'event', attributes: { properties: props, metric: { data: { type: 'metric', attributes: { name: 'Label Design Saved' } } }, profile, unique_id: `${state.designId}-${Date.now()}` } } };
          const r = await fetch(`https://a.klaviyo.com/client/events/?company_id=${encodeURIComponent(key)}`, { method: 'POST', headers, body: JSON.stringify(ev) });
          if (!r.ok && r.status !== 202) throw new Error('klaviyo ' + r.status);
        }
        sent = true;
      } catch (err) { console.error('Label builder: could not save the design to email', err); }
      saveBusy = false;
      if (!sent) { el.saveStatus.textContent = 'That did not go through. Try again, or use "Download this picture".'; return; }
      track('design_emailed', { quantity: state.qty });
      el.saveStatus.replaceChildren('Sent. Check your inbox for the link. ', h('a', { href: link }, 'Open it now'));
      el.saveEmail.value = '';
    }

    function updateNav() {
      const i = state.step, last = i === STEPS.length - 1;
      el.back.hidden = i === 0;
      el.next.hidden = last;
      const nextLabel = last ? '' : (STEPS[i].id === 'qr' && state.qrWanted == null ? 'Skip the QR code' : `Next: ${STEPS[i + 1].label}`);
      el.next.textContent = nextLabel;
      el.mbNext.textContent = last ? 'Get my free proof' : nextLabel;
      el.mbNext.onclick = last ? () => proofForm.form.requestSubmit() : () => goTo(i + 1);
    }
    function goTo(i) {
      i = clamp(i, 0, STEPS.length - 1);
      if (state.submitted) return;
      // Moving on from the QR step without choosing means no QR code.
      if (STEPS[state.step].id === 'qr' && i > state.step && state.qrWanted == null) setQrWanted(false);
      state.step = i;
      STEPS.forEach((s, j) => { panels[s.id].hidden = j !== i; });
      root.querySelectorAll('.cclb-progress__step').forEach((b, j) => {
        b.classList.toggle('is-done', j < i);
        if (j === i) b.setAttribute('aria-current', 'step'); else b.removeAttribute('aria-current');
      });
      el.progressBar.style.width = `${(i / (STEPS.length - 1)) * 100}%`;
      const ol = root.querySelector('.cclb-progress ol'), cur = ol && ol.children[i];
      if (ol && cur && ol.scrollWidth > ol.clientWidth) ol.scrollTo({ left: Math.max(0, cur.offsetLeft - 12), behavior: reduceMotion ? 'auto' : 'smooth' });
      root.querySelector('.cclb-mbar').hidden = STEPS[i].id === 'proof';
      el.estimateLine.hidden = STEPS[i].id === 'proof';
      el.panelTitle.textContent = STEPS[i].title;
      updateNav();
      if (STEPS[i].id === 'style') refreshThumbs();
      if (STEPS[i].id === 'proof') { renderPrice(); updateCartButton(); }
      showChecks();
      el.panelTitle.focus({ preventScroll: true });
      if (window.innerWidth < 960) root.querySelector('.cclb-panel').scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'start' });
      saveDraft();
    }

    /* ---------- Choices ---------- */
    async function setProduct(id) {
      if (!cfg.products.some(p => p.id === id)) return;
      state.productId = id;
      root.querySelectorAll('[data-product]').forEach(l => { l.classList.toggle('is-checked', l.dataset.product === id); l.querySelector('input').checked = l.dataset.product === id; });
      renderSizeToggle();
      thumbCache.clear();
      await rebuild();
      renderPrice(); updateCartButton(); refreshThumbs(); saveDraft();
      track('bag_selected');
      announce(`${product().name} selected.`);
    }
    async function setStyle(id) {
      state.styleId = id;
      el.styleCards.querySelectorAll('[data-style]').forEach(l => l.classList.toggle('is-checked', l.dataset.style === id));
      await rebuild();
      refreshColorSelection(); saveDraft();
      track('style_selected');
      announce(`${currentStyle().name} style selected.`);
    }
    async function setFlavor(id) {
      state.flavorId = id;
      el.flavorGrid.querySelectorAll('.cclb-flavor').forEach(l => l.classList.toggle('is-checked', l.querySelector('input').value === id));
      thumbCache.clear();
      await rebuild();
      saveDraft();
      track('flavor_selected');
      announce(`${flavor().name} selected.`);
    }
    const setColor = debounce(async (hex, picked) => {
      state.color = hex;
      if (picked) state.colorPicked = true;
      thumbCache.clear();
      refreshColorSelection();
      await rebuild();
      refreshThumbs(); saveDraft();
      track('color_changed', { color: hex });
    }, 60);

    /* ------------------------------------------------------------------
       Saving drafts, and opening a saved design
       ------------------------------------------------------------------ */
    const DRAFT_KEY = 'cclb-draft-v2';
    function designSnapshot() {
      return {
        v: 2, productId: state.productId, styleId: state.styleId, flavorId: state.flavorId, color: state.color, colorPicked: state.colorPicked,
        message: state.message, qrWanted: state.qrWanted, qrUrl: state.qrUrl, qrCaption: state.qrCaption, logoScale: state.logoScale, qty: state.qty,
        logo: state.logo ? { kind: state.logo.kind, name: state.logo.name, type: state.logo.type, size: state.logo.size } : null
      };
    }
    // The design plus its logo file, so it can be reopened later on any device.
    function designForSave(limit = 6e6) {
      const snap = designSnapshot();
      if (state.logo && state.logo.originalDataUrl.length < limit) snap.logoData = { url: state.logo.originalDataUrl, preview: state.logo.previewDataUrl || null };
      return snap;
    }
    const saveDraft = debounce(() => {
      if (state.submitted) return;
      if (!store.set(DRAFT_KEY, { at: Date.now(), step: state.step, design: designForSave(2.5e6) })) store.set(DRAFT_KEY, { at: Date.now(), step: state.step, design: designSnapshot() });
    }, 800);
    async function applySnapshot(snap) {
      Object.assign(state, {
        productId: cfg.products.some(p => p.id === snap.productId) ? snap.productId : state.productId,
        styleId: byStyle[snap.styleId] ? snap.styleId : state.styleId,
        flavorId: byFlavor[snap.flavorId] ? snap.flavorId : state.flavorId,
        color: snap.color && isHex(snap.color) ? snap.color : null, colorPicked: !!snap.colorPicked,
        message: snap.message || '', qrWanted: snap.qrWanted === true ? true : snap.qrWanted === false ? false : null,
        qrUrl: snap.qrUrl || '', qrCaption: snap.qrCaption == null ? state.qrCaption : snap.qrCaption,
        logoScale: clamp(num(snap.logoScale, 1), 0.6, 1), qty: snap.qty || state.qty
      });
      if (snap.logo && snap.logoData && snap.logoData.url) {
        const logo = { ...snap.logo, originalDataUrl: snap.logoData.url, previewDataUrl: snap.logoData.preview || null };
        try { const { obj, colors } = await logoObjectFrom(logo); Object.assign(state, { logo, logoObj: obj, logoColors: colors }); }
        catch (e) { console.warn('Label builder: saved logo could not be read', e); }
      }
      el.msg.value = state.message; el.msgCount.textContent = `${state.message.length}/${cfg.limits.messageMaxChars}`;
      el.qrUrl.value = state.qrUrl; el.qrCaption.value = state.qrCaption;
      syncQrChoice();
      root.querySelectorAll('[data-product]').forEach(l => { l.classList.toggle('is-checked', l.dataset.product === state.productId); l.querySelector('input').checked = l.dataset.product === state.productId; });
      el.styleCards.querySelectorAll('[data-style]').forEach(l => { l.classList.toggle('is-checked', l.dataset.style === state.styleId); l.querySelector('input').checked = l.dataset.style === state.styleId; });
      thumbCache.clear();
      renderSizeToggle(); renderFlavors(); renderLogoPanel(); buildColors();
      await rebuild();
      setQty(state.qty); syncWarnings(); refreshThumbs(); updateNav();
    }
    async function openSavedDesign(id) {
      if (!cfg.lead.backendUrl) return false;
      try {
        const data = await (await fetch(`${cfg.lead.backendUrl}?design=${encodeURIComponent(id)}`)).json();
        if (!data || !data.ok || !data.design) throw new Error('not found');
        await applySnapshot(data.design);
        state.parentDesignId = id;
        toast(`Opened design ${id}. Changes save as a new design, so the original stays as it was.`);
        track('design_reloaded', { parent_design_id: id });
        return true;
      } catch (e) {
        toast('We couldn\u2019t find that design. Start a new one, or call us and we\u2019ll pull it up.');
        return false;
      }
    }

    /* ------------------------------------------------------------------
       Proof request
       ------------------------------------------------------------------ */
    function validate(f) {
      let ok = true;
      const set = (n, msg) => {
        const e = f[n + 'Err']; if (!e) return;
        e.textContent = msg || ''; e.hidden = !msg; f[n].setAttribute('aria-invalid', msg ? 'true' : 'false');
        if (msg) { f[n].setAttribute('aria-describedby', e.id); ok = false; } else f[n].removeAttribute('aria-describedby');
      };
      set('qty', Math.round(num(f.qty.value, 0)) >= cfg.pricing.minQty ? '' : `The minimum is ${cfg.pricing.minQty} bags.`);
      set('first', f.first.value.trim() ? '' : 'Add your first name.');
      set('email', /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(f.email.value.trim()) ? '' : 'Add an email we can send your proof to, like you@company.com.');
      const digits = f.phone.value.replace(/\D/g, '');
      set('phone', !f.phone.value.trim() || digits.length >= 10 ? '' : 'Add a 10-digit phone number, or leave it blank.');
      return ok;
    }
    function firstTouch() {
      const q = new URLSearchParams(location.search);
      let ft = {};
      try { ft = JSON.parse(localStorage.getItem('cc_first_touch') || '{}') || {}; } catch (e) { ft = {}; }
      const gclid = (() => { try { return (localStorage.getItem('cc_gclid') || '').split('|')[0]; } catch (e) { return ''; } })() || q.get('gclid') || '';
      return {
        page_url: location.href.split('#')[0], lead_first_landing: ft.landing || location.pathname,
        lead_first_referrer: ft.referrer || ft.ref || (document.referrer ? document.referrer.split('/')[2] : 'none'),
        utm_source: ft.utm_source || ft.us || q.get('utm_source') || '', utm_medium: ft.utm_medium || ft.um || q.get('utm_medium') || '',
        utm_campaign: ft.utm_campaign || ft.uc || q.get('utm_campaign') || '', lead_first_seen: ft.at || ft.ts || '', gclid
      };
    }
    let exportModule = null;
    async function loadExport() {
      if (!exportModule) {
        await loadScript(A('cc-label-vendor-jspdf.umd.min.js'));
        await loadScript(A('cc-label-vendor-svg2pdf.umd.min.js'));
        await loadScript(A('cc-label-export.js'));
        exportModule = window.CCLabelExport;
      }
      return exportModule;
    }
    // Everything the export file needs to draw the proof.
    function exportContext() {
      return {
        fabric, canvas, cfg, state, A, loadScript, ensureFont, fontByFamily, fontData,
        geometry: { B, TW, TH, CW, CH, PT, zoom: 1 },
        setExporting: v => { exporting = v; },
        labelImage, drawBag, designSnapshot, flavor, product, style: currentStyle, dpiStatus, estimate,
        lastChecks: () => lastChecks, netWeightText, ingredientsText
      };
    }
    function setStatus(text, level) {
      proofForm.status.replaceChildren(text ? h('p', { class: 'cclb-check cclb-check--' + (level || 'info') }, h('span', { class: 'cclb-check__icon', 'aria-hidden': 'true' }, ICON[level || 'info']), h('span', null, text)) : '');
    }
    const testEmail = email => /\+test[^@]*@/i.test(email);
    function withCompany(contact) {
      return { ...contact, company: contact.company || [contact.first, contact.last].filter(Boolean).join(' ') || 'No company given' };
    }
    async function klaviyoDirect(contact, props) {
      const key = cfg.lead.klaviyoPublicKey;
      if (!key) return { sent: false, why: 'no key' };
      if (cfg.lead.testMode && !testEmail(contact.email)) return { sent: false, why: 'test mode' };
      const headers = { 'Content-Type': 'application/json', revision: '2024-10-15' };
      const profile = { data: { type: 'profile', attributes: { email: contact.email, first_name: contact.first, last_name: contact.last, organization: contact.company, properties: { phone_raw: contact.phone || undefined, lead_source: 'Label Builder' } } } };
      const ev = { data: { type: 'event', attributes: { properties: props, metric: { data: { type: 'metric', attributes: { name: 'Label Proof Requested' } } }, profile, unique_id: props.design_id } } };
      const r1 = await fetch(`https://a.klaviyo.com/client/events/?company_id=${encodeURIComponent(key)}`, { method: 'POST', headers, body: JSON.stringify(ev) });
      if (!r1.ok && r1.status !== 202) throw new Error('klaviyo event ' + r1.status);
      if (contact.subscribe && cfg.lead.klaviyoListId) {
        const sub = { data: { type: 'subscription', attributes: { custom_source: 'Label Builder', profile: { data: { type: 'profile', attributes: { email: contact.email } } } }, relationships: { list: { data: { type: 'list', id: cfg.lead.klaviyoListId } } } } };
        const r2 = await fetch(`https://a.klaviyo.com/client/subscriptions/?company_id=${encodeURIComponent(key)}`, { method: 'POST', headers, body: JSON.stringify(sub) });
        if (!r2.ok && r2.status !== 202) throw new Error('klaviyo subscribe ' + r2.status);
      }
      return { sent: true };
    }
    async function submitProof() {
      const f = proofForm;
      if (state.busy || f.hp.value) return;
      if (!validate(f)) { const bad = f.form.querySelector('[aria-invalid="true"]'); if (bad) bad.focus(); return; }
      if (qrOn() && !normalizeUrl(state.qrUrl)) { setStatus('Add the link for your QR code, or pick "No QR code," before we make your proof.', 'warn'); return; }
      state.busy = true;
      f.submit.disabled = true; f.submit.textContent = 'Building your proof\u2026';
      state.designId = newDesignId();
      const contact = {
        first: f.first.value.trim(), last: f.last.value.trim(), email: f.email.value.trim(), company: f.company.value.trim(), phone: f.phone.value.trim(),
        subscribe: cfg.lead.subscribeMode === 'always' || f.consent.checked
      };
      setQty(Math.round(num(f.qty.value, state.qty)));
      const e = estimate();
      const order = {
        quantity: state.qty, product: product().id, product_name: product().name, flavor: flavor().name, flavor_id: flavor().id, style_id: state.styleId,
        label_color: styleColors().accent, in_hands_date: f.date.value || '', order_type: f.type.value, notes: f.notes.value.trim(),
        est_price_per_bag: e && Number.isFinite(e.unit) ? (e.unit / 100).toFixed(2) : '', est_total: e && Number.isFinite(e.total) ? (e.total / 100).toFixed(2) : '',
        qr_url: qrOn() ? normalizeUrl(state.qrUrl) : '', qr_caption: qrOn() ? state.qrCaption.trim() : '', message: state.message.trim()
      };
      const dep = depositFor();
      if (dep) Object.assign(order, { deposit_percent: dep.percent, deposit_amount: (dep.cents / 100).toFixed(2), deposit_url: depositUrl(dep, state.designId) });
      const dpi = dpiStatus();
      const meta = { ...firstTouch(), logo_dpi_status: dpi.level, logo_dpi: dpi.dpi || null, checks: runChecks().map(c => `${c.level}: ${c.text}`), fda_mode: cfg.fda.mode, test_mode: !!cfg.lead.testMode, parent_design_id: state.parentDesignId || '' };
      let outputs = null, saved = null, problem = null;
      try {
        setStatus('Building your proof PDF and bag picture\u2026');
        outputs = await (await loadExport()).buildOutputs(exportContext(), { designId: state.designId, contact, order, meta });
      } catch (err) { console.error('Label builder: export failed', err); problem = 'export'; }
      const props = { design_id: state.designId, ...order, product: order.product_name, logo_dpi_status: meta.logo_dpi_status, page_url: meta.page_url, utm_source: meta.utm_source, utm_medium: meta.utm_medium, utm_campaign: meta.utm_campaign };
      if (cfg.lead.backendUrl) {
        try {
          setStatus('Saving your design\u2026');
          // The company field is optional, but the proof service files each request under a company, so use the name.
          const payload = { action: 'proof', designId: state.designId, contact: withCompany(contact), order, meta, design: designForSave(), files: outputs ? outputs.files : {}, hp: f.hp.value };
          const res = await fetch(cfg.lead.backendUrl, { method: 'POST', headers: { 'Content-Type': 'text/plain;charset=utf-8' }, body: JSON.stringify(payload) });
          saved = await res.json();
          if (!saved || !saved.ok) throw new Error((saved && saved.error) || 'save failed');
          Object.assign(props, { proof_pdf_url: saved.proofPdfUrl || '', mockup_png_url: saved.mockupPngUrl || '' });
        } catch (err) { console.error('Label builder: proof service failed', err); saved = null; problem = problem || 'backend'; }
      }
      // Without the proof service, the browser tells Klaviyo itself, so the lead still lands.
      let klaviyoSent = false;
      if (!saved || !saved.klaviyo) {
        try { const r = await klaviyoDirect(contact, props); klaviyoSent = r.sent || r.why === 'test mode'; }
        catch (err) { console.error('Label builder: Klaviyo failed', err); problem = problem || 'klaviyo'; }
      }
      if (!saved) store.set('cclb-retry-' + state.designId, { at: Date.now(), contact, order, meta, props, design: designSnapshot() });
      state.busy = false;
      if (!saved && !klaviyoSent) {
        f.submit.disabled = false; f.submit.textContent = 'Get my free proof';
        setStatus(`That did not go through. Try again, or email info@cactuscorn.com with design ID ${state.designId} and we will sort it out.`, 'bad');
        track('proof_request_failed', { reason: problem || 'unknown' });
        return;
      }
      if (problem) track('proof_request_failed', { reason: problem, recovered: true });
      track('proof_requested', { quantity: state.qty, est_total: order.est_total });
      if (!cfg.lead.testMode) (window.dataLayer = window.dataLayer || []).push({ event: 'generate_lead', lead_type: 'corporate_gifting', lead_form: 'label_builder', design_id: state.designId });
      store.del(DRAFT_KEY);
      showConfirmation(outputs, saved);
    }
    // Requests that never reached the proof service are kept on the device and sent again later.
    async function flushRetries() {
      if (!cfg.lead.backendUrl) return;
      let keys = [];
      try { keys = Object.keys(localStorage).filter(k => k.startsWith('cclb-retry-')); } catch (e) { return; }
      for (const key of keys) {
        const item = store.get(key, null);
        if (!item || Date.now() - item.at > 14 * 864e5) { store.del(key); continue; }
        try {
          const res = await fetch(cfg.lead.backendUrl, { method: 'POST', headers: { 'Content-Type': 'text/plain;charset=utf-8' }, body: JSON.stringify({ action: 'proof', retry: true, designId: key.replace('cclb-retry-', ''), contact: withCompany(item.contact || {}), order: item.order, meta: item.meta, design: item.design, files: {} }) });
          const data = await res.json();
          if (data && data.ok) store.del(key);
        } catch (e) { /* try again next visit */ }
      }
    }
    function showConfirmation(outputs, saved) {
      state.submitted = state.designId;
      const link = `${location.origin}${location.pathname}?design=${state.designId}`;
      const actions = h('div', { class: 'cclb-actions' });
      if (outputs && outputs.mockupUrl) actions.append(h('a', { class: 'cclb-btn', href: outputs.mockupUrl, download: `cactus-corn-label-${state.designId}.png`, onclick: () => track('mockup_downloaded') }, 'Download the bag picture'));
      if (outputs && outputs.proofUrl && cfg.lead.testMode) actions.append(h('a', { class: 'cclb-btn cclb-btn--ghost', href: outputs.proofUrl, download: `cactus-corn-proof-${state.designId}.pdf` }, 'Download the proof PDF (test mode)'));
      if (outputs && outputs.printUrl && cfg.lead.testMode) actions.append(h('a', { class: 'cclb-btn cclb-btn--ghost', href: outputs.printUrl, download: `${state.designId}-print-${cfg.print.trimIn[0]}x${cfg.print.trimIn[1]}.pdf` }, 'Download the print file (test mode)'));
      actions.append(h('button', { type: 'button', class: 'cclb-btn cclb-btn--ghost', onclick: () => { location.href = location.pathname; } }, 'Start another design'));
      const box = h('div', { class: 'cclb-confirm', tabindex: '-1' },
        h('h2', { class: 'cclb-title' }, 'Got it. Your proof is on the way.'),
        h('p', { class: 'cclb-lede' }, `We will follow up within ${cfg.pricing.proofTurnaround}.`),
        depositBox(),
        h('p', null, 'Your design ID is ', h('strong', null, state.designId), '. Keep it handy if you call us.'),
        saved ? h('p', null, 'Open this design again anytime: ', h('a', { href: link }, link)) : h('p', { class: 'cclb-help' }, 'We saved your request. If the proof files are slow to arrive, we have your design ID.'),
        outputs && outputs.mockupUrl ? h('img', { class: 'cclb-confirm__img', src: outputs.mockupUrl, alt: 'Your label on the bag', width: 640, height: 800 }) : null,
        actions);
      root.querySelector('.cclb-main').replaceChildren(box);
      root.querySelector('.cclb-progress').hidden = true;
      root.querySelector('.cclb-mbar').hidden = true;
      box.focus();
      window.scrollTo({ top: root.getBoundingClientRect().top + window.scrollY - 20, behavior: reduceMotion ? 'auto' : 'smooth' });
    }

    /* ---------- Order now (cart) ---------- */
    function cartVariant() {
      const p = product(), e = estimate();
      if (!cfg.cart.enabled || !e) return null;
      const want = [p.name.toLowerCase(), String(e.tier.min)];
      return (cfg.cart.variants || []).find(x => x.available && want.every(w => (x.title || '').toLowerCase().includes(w))) || null;
    }
    function updateCartButton() { if (proofForm) proofForm.cart.hidden = !cartVariant(); }
    async function addToCart() {
      const v = cartVariant();
      if (!v) return;
      track('add_to_cart_clicked');
      state.designId = state.designId || newDesignId();
      try {
        const res = await fetch(cfg.cart.addUrl, { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ items: [{ id: v.id, quantity: 1, properties: { 'Design ID': state.designId, Flavor: flavor().name, 'In-hands date': proofForm.date.value || '' } }] }) });
        if (!res.ok) throw new Error('cart');
        location.href = cfg.cart.cartUrl;
      } catch (e) { toast('We couldn\u2019t add that to the cart. Request a proof instead and we\u2019ll set up the order.'); }
    }

    /* ---------- Deposit (pay part now at the normal checkout) ---------- */
    // One deposit variant per bag and order size, priced at the deposit share of the price per bag.
    // The builder offers it only when that price matches its own price table, so nobody is charged the wrong amount.
    function depositPercent() { return num(cfg.deposit && cfg.deposit.percent, 50); }
    function depositOn() { return !!(cfg.deposit && cfg.deposit.enabled && (cfg.deposit.variants || []).length); }
    function depositFor(qty = state.qty) {
      const e = estimate(qty), p = product();
      if (!depositOn() || !e || !Number.isFinite(e.unit)) return null;
      const v = (cfg.deposit.variants || []).find(x => x.available && x.options
        && String(x.options[0]).trim().toLowerCase() === p.name.trim().toLowerCase()
        && String(x.options[1]).trim().indexOf(e.tier.min + ' ') === 0);
      if (!v) return null;
      if (Math.abs(v.priceCents - e.unit * depositPercent() / 100) > 1) { console.warn('Label builder: the deposit price does not match the price table', v); return null; }
      return { variant: v, percent: depositPercent(), cents: v.priceCents * qty };
    }
    // A cart link goes straight to checkout with only the deposit in it, and works the same from the proof email.
    function depositUrl(dep, designId, qty = state.qty) {
      const q = new URLSearchParams();
      q.set('attributes[Design ID]', designId);
      q.set('attributes[Deposit for]', `${qty.toLocaleString('en-US')} ${product().name.toLowerCase()}s, ${flavor().name}`);
      return `${String(cfg.deposit.shopUrl || location.origin).replace(/\/+$/, '')}/cart/${dep.variant.id}:${qty}?${q.toString()}`;
    }
    function depositBox() {
      const dep = depositFor();
      if (!dep) return null;
      return h('div', { class: 'cclb-deposit' },
        h('h3', { class: 'cclb-deposit__title' }, 'Hold your production slot'),
        h('p', null, `Pay the ${dep.percent}% deposit now: `, h('strong', null, money(dep.cents)), ` for ${state.qty.toLocaleString('en-US')} ${product().name.toLowerCase()}s.`),
        h('a', { class: 'cclb-btn cclb-btn--big', href: depositUrl(dep, state.designId), onclick: () => track('deposit_checkout_clicked', { quantity: state.qty, deposit: (dep.cents / 100).toFixed(2) }) }, `Pay the ${money(dep.cents)} deposit`),
        h('p', { class: 'cclb-help' }, ['Shop Pay, PayPal, Amazon Pay, Google Pay, or card at checkout.', cfg.deposit.terms || ''].filter(Boolean).join(' ')),
        h('p', { class: 'cclb-help' }, 'Not ready yet? We\u2019ll send this link with your proof.'));
    }

    /* ------------------------------------------------------------------
       Start
       ------------------------------------------------------------------ */
    renderSizeToggle(); renderFlavors(); renderLogoPanel(); buildStyleCards(); buildColors();
    await rebuild();
    setQty(state.qty);
    goTo(0);
    track('label_builder_opened');

    const wanted = params.get('design'), packed = params.get('d');
    const fromLink = packed ? unpackDesign(packed) : null;
    if (wanted && /^CC-[A-Z0-9]{6}$/.test(wanted)) await openSavedDesign(wanted);
    else if (fromLink) {
      const draft = store.get(DRAFT_KEY, null);
      // The same device still has the logo file in its draft; a new device gets everything but the logo.
      await applySnapshot(draft && draft.design && draft.design.logoData ? { ...fromLink, logo: draft.design.logo, logoData: draft.design.logoData } : fromLink);
      toast(state.logo ? 'Your design is back. Pick up where you left off.' : 'Your design is back. Add your logo again on step 2.');
      track('design_reloaded', { from: 'email_link' });
    }
    else {
      const draft = store.get(DRAFT_KEY, null);
      if (draft && draft.design && Date.now() - draft.at < 30 * 864e5) {
        const resume = h('button', { type: 'button', class: 'cclb-chip', onclick: async () => {
          el.toast.classList.remove('is-shown');
          await applySnapshot(draft.design);
          if (Number.isInteger(draft.step) && draft.step > 0) goTo(draft.step);
        } }, 'Pick up where you left off');
        el.toast.replaceChildren(h('span', null, 'You have a label in progress. '), resume);
        el.toast.classList.add('is-shown');
        clearTimeout(toastTimer); toastTimer = setTimeout(() => el.toast.classList.remove('is-shown'), 9000);
      }
    }
    flushRetries();
    window.addEventListener('pageshow', e => { if (e.persisted && state.busy) { state.busy = false; proofForm.submit.disabled = false; proofForm.submit.textContent = 'Get my free proof'; } });
    if (cfg.lead.testMode) window.CCLB_DEBUG = { state, canvas, cfg, runChecks, estimate, letterRatios, rebuild, setFlavor, setStyle, setProduct, setQty, setColor, setQrWanted, handleLogoFile, exportContext, loadExport, dpiStatus, normalizeUrl, goTo, drawBag, labelImage, styleColors, sceneBox, setView };
  }

  function initAll(scope) {
    scope.querySelectorAll('[data-cclb]').forEach(root => {
      if (root.dataset.cclbReady) return;
      root.dataset.cclbReady = 'true';
      initBuilder(root).catch(err => console.error('Label builder failed to start.', err));
    });
  }
  initAll(document);
  document.addEventListener('shopify:section:load', e => initAll(e.target));
})();
